import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useTheme } from '../../contexts/ThemeContext';

interface CompRate {
  id: string;
  broker: string;
  comp_percentage: number;
  personal_comp_percentage: number;
  full_broker_name: string;
  email_address?: string;
}

interface ClientState {
  id: string;
  state_name: string;
  display_order: number;
  user_id?: string | null;
  is_global?: boolean;
}

interface LenderName {
  id: string;
  lender_name: string;
  display_order: number;
  user_id?: string | null;
  is_global?: boolean;
}

interface LoanType {
  id: string;
  loan_type: string;
  display_order: number;
  intake_mapping: string;
}

interface LeadSource {
  id: string;
  name: string;
  email: string;
  phone: string;
  state: string;
  display_order: number;
  user_id?: string | null;
  is_global?: boolean;
}

interface PageOrder {
  id: string;
  page_name: string;
  display_order: number;
}

interface DiscoveryCallField {
  id: string;
  field_name: string;
  field_label: string;
  field_type: string;
  dropdown_options: string[] | null;
  display_order: number;
  is_required: boolean;
}

interface QuickFilterConfig {
  id: string;
  filter_name: string;
  client_states: string[];
  display_order?: number;
  user_id?: string | null;
  is_global?: boolean;
}

interface HiddenGlobalSetting {
  id: string;
  user_id: string;
  setting_type: string;
  setting_id: string;
}

interface DiscoveryCallField {
  id: string;
  field_name: string;
  field_label: string;
  field_type: string;
  dropdown_options: string[] | null;
  display_order: number;
  is_required: boolean;
}

interface PreApprovalTemplate {
  id: string;
  template_content: string;
  user_id: string;
  updated_at: string;
}

export default function AdminPage() {
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useTheme();
  
  // Add settings view toggle state
  const [settingsView, setSettingsView] = useState<'personal' | 'global'>('personal');
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<'admin' | 'MLO' | null>(null);
  
  // Add mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Add profile state
  const [userProfile, setUserProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    nmls: ''
  });
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileSaveMessage, setProfileSaveMessage] = useState('');
  
  const [compRates, setCompRates] = useState<CompRate[]>([]);
  const [clientStates, setClientStates] = useState<ClientState[]>([]);
  const [lenderNames, setLenderNames] = useState<LenderName[]>([]);
  const [loanTypes, setLoanTypes] = useState<LoanType[]>([]);
  const [leadSources, setLeadSources] = useState<LeadSource[]>([]);
  const [pageOrders, setPageOrders] = useState<PageOrder[]>([]);
  const [discoveryFields, setDiscoveryFields] = useState<DiscoveryCallField[]>([]);
  
  // Add pre-approval template state
  const [preApprovalTemplate, setPreApprovalTemplate] = useState<string>('');
  const [editingPreApproval, setEditingPreApproval] = useState(false);
  const [preApprovalSaveMessage, setPreApprovalSaveMessage] = useState('');

  // Add quick filter configuration state
  const [quickFilterConfigs, setQuickFilterConfigs] = useState<QuickFilterConfig[]>([]);

  // Add hidden global settings state
  const [hiddenGlobalSettings, setHiddenGlobalSettings] = useState<HiddenGlobalSetting[]>([]);

  // Add state for quick filter configuration
  const [showAddQuickFilterForm, setShowAddQuickFilterForm] = useState(false);
  const [newQuickFilterName, setNewQuickFilterName] = useState('');

  // Add state for discovery call preview modal
  const [showDiscoveryPreview, setShowDiscoveryPreview] = useState(false);

  // ADD MISSING STATE VARIABLES
  const [showAddCompForm, setShowAddCompForm] = useState(false);
  const [newCompRate, setNewCompRate] = useState({
    broker: '',
    comp_percentage: '',
    personal_comp_percentage: '',
    full_broker_name: '',
    email_address: ''
  });
  const [editingCompId, setEditingCompId] = useState<string | null>(null);

  const [showAddStateForm, setShowAddStateForm] = useState(false);
  const [newClientState, setNewClientState] = useState({
    state_name: '',
    display_order: ''
  });
  const [editingStateId, setEditingStateId] = useState<string | null>(null);

  const [showAddLenderForm, setShowAddLenderForm] = useState(false);
  const [newLenderName, setNewLenderName] = useState({
    lender_name: '',
    display_order: ''
  });
  const [editingLenderId, setEditingLenderId] = useState<string | null>(null);

  const [showAddLoanTypeForm, setShowAddLoanTypeForm] = useState(false);
  const [newLoanType, setNewLoanType] = useState({
    loan_type: '',
    display_order: '',
    intake_mapping: ''
  });
  const [editingLoanTypeId, setEditingLoanTypeId] = useState<string | null>(null);

  const [showAddLeadSourceForm, setShowAddLeadSourceForm] = useState(false);
  const [newLeadSource, setNewLeadSource] = useState({
    name: '',
    email: '',
    phone: '',
    state: '',
    display_order: ''
  });
  const [editingLeadSourceId, setEditingLeadSourceId] = useState<string | null>(null);

  const [showAddDiscoveryFieldForm, setShowAddDiscoveryFieldForm] = useState(false);
  const [newDiscoveryField, setNewDiscoveryField] = useState({
    field_name: '',
    field_label: '',
    field_type: 'text',
    dropdown_options: '',
    is_required: false
  });
  const [editingDiscoveryFieldId, setEditingDiscoveryFieldId] = useState<string | null>(null);

  const [editingQuickFilterId, setEditingQuickFilterId] = useState<string | null>(null);

  // Check if user is admin
  const isAdmin = userRole === 'admin';

  // Check authentication
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
      } else {
        setCurrentUserId(session.user.id);
        // Load user profile
        const metadata = session.user.user_metadata;
        setUserProfile({
          firstName: metadata.first_name || '',
          lastName: metadata.last_name || '',
          email: session.user.email || '',
          phone: metadata.phone || '',
          nmls: metadata.nmls || ''
        });
        
        // Load user role from user_profiles table
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('user_id', session.user.id)
          .maybeSingle();
        
        if (profileData) {
          setUserRole(profileData.role as 'admin' | 'MLO');
        } else {
          // If no profile exists, create one with default MLO role
          const { data: newProfile } = await supabase
            .from('user_profiles')
            .insert({ user_id: session.user.id, role: 'MLO' })
            .select('role')
            .single();
          
          if (newProfile) {
            setUserRole(newProfile.role as 'admin' | 'MLO');
          } else {
            setUserRole('MLO');
          }
        }
      }
    };
    checkAuth();
  }, [navigate]);

  // Load all data
  useEffect(() => {
    loadCompRates();
    loadClientStates();
    loadLenderNames();
    loadLoanTypes();
    loadLeadSources();
    loadPageOrders();
    loadDiscoveryFields();
    loadQuickFilterConfigs();
    loadPreApprovalTemplate();
    loadHiddenGlobalSettings();
  }, []);

  // Reload data when settings view changes
  useEffect(() => {
    if (currentUserId) {
      loadClientStates();
      loadLenderNames();
      loadLeadSources();
      loadQuickFilterConfigs();
      loadHiddenGlobalSettings();
    }
  }, [settingsView, currentUserId]);

  const loadPageOrders = async () => {
    // Initialize default page orders if they don't exist
    const defaultPages = [
      { page_name: 'Closed', display_order: 1 },
      { page_name: 'Dead Deals', display_order: 2 },
      { page_name: 'On Hold', display_order: 3 },
      { page_name: 'Templates', display_order: 4 },
      { page_name: 'Message Intake', display_order: 5 },
      { page_name: 'History', display_order: 6 },
      { page_name: 'Admin', display_order: 7 }
    ];

    setPageOrders(defaultPages.map((page, index) => ({
      id: `page-${index}`,
      ...page
    })));
  };

  // ----- Page Order handlers (single definition) -----
  const handleMovePageUp = (id: string) => {
    const currentIndex = pageOrders.findIndex(p => p.id === id);
    if (currentIndex <= 0) return;

    const newOrders = [...pageOrders];
    [newOrders[currentIndex - 1], newOrders[currentIndex]] = [newOrders[currentIndex], newOrders[currentIndex - 1]];
    
    // Update display orders
    newOrders.forEach((page, index) => {
      page.display_order = index + 1;
    });

    setPageOrders(newOrders);
  };

  const handleMovePageDown = (id: string) => {
    const currentIndex = pageOrders.findIndex(p => p.id === id);
    if (currentIndex >= pageOrders.length - 1) return;

    const newOrders = [...pageOrders];
    [newOrders[currentIndex], newOrders[currentIndex + 1]] = [newOrders[currentIndex + 1], newOrders[currentIndex]];
    
    // Update display orders
    newOrders.forEach((page, index) => {
      page.display_order = index + 1;
    });

    setPageOrders(newOrders);
  };
  // --------------------------------------------------

  const loadHiddenGlobalSettings = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { data, error } = await supabase
      .from('hidden_global_settings')
      .select('*')
      .eq('user_id', session.user.id);

    if (error) {
      console.error('Error loading hidden global settings:', error);
      return;
    }

    if (data) {
      setHiddenGlobalSettings(data);
    }
  };

  const isGlobalSettingHidden = (settingType: string, settingId: string): boolean => {
    return hiddenGlobalSettings.some(
      h => h.setting_type === settingType && h.setting_id === settingId
    );
  };

  const handleToggleGlobalSettingVisibility = async (settingType: string, settingId: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const isCurrentlyHidden = isGlobalSettingHidden(settingType, settingId);

    if (isCurrentlyHidden) {
      // Unhide - delete the record
      const { error } = await supabase
        .from('hidden_global_settings')
        .delete()
        .eq('user_id', session.user.id)
        .eq('setting_type', settingType)
        .eq('setting_id', settingId);

      if (error) {
        console.error('Error unhiding setting:', error);
        alert('Failed to show setting');
        return;
      }

      setHiddenGlobalSettings(prev => 
        prev.filter(h => !(h.setting_type === settingType && h.setting_id === settingId))
      );
    } else {
      // Hide - insert a record
      const { data, error } = await supabase
        .from('hidden_global_settings')
        .insert({
          user_id: session.user.id,
          setting_type: settingType,
          setting_id: settingId
        })
        .select()
        .single();

      if (error) {
        console.error('Error hiding setting:', error);
        alert('Failed to hide setting');
        return;
      }

      if (data) {
        setHiddenGlobalSettings(prev => [...prev, data]);
      }
    }
  };

  const loadCompRates = async () => {
    const { data, error } = await supabase
      .from('comp_rates')
      .select('*')
      .order('broker', { ascending: true });

    if (error) {
      console.error('Error loading comp rates:', error);
      return;
    }

    if (data) {
      setCompRates(data);
    }
  };

  const loadClientStates = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    let query = supabase
      .from('client_states')
      .select('*');

    if (settingsView === 'personal') {
      // Load personal states and global defaults
      query = query.or(`user_id.eq.${session.user.id},is_global.eq.true`);
    } else {
      // Load only global defaults
      query = query.eq('is_global', true);
    }

    const { data, error } = await query.order('display_order', { ascending: true });

    if (error) {
      console.error('Error loading client states:', error);
      return;
    }

    if (data) {
      setClientStates(data);
    }
  };

  const loadLenderNames = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    let query = supabase
      .from('lender_names')
      .select('*');

    if (settingsView === 'personal') {
      // Load personal lenders and global defaults
      query = query.or(`user_id.eq.${session.user.id},is_global.eq.true`);
    } else {
      // Load only global defaults
      query = query.eq('is_global', true);
    }

    const { data, error } = await query.order('display_order', { ascending: true });

    if (error) {
      console.error('Error loading lender names:', error);
      return;
    }

    if (data) {
      setLenderNames(data);
    }
  };

  const loadLoanTypes = async () => {
    const { data, error } = await supabase
      .from('loan_types')
      .select('*')
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error loading loan types:', error);
      return;
    }

    if (data) {
      setLoanTypes(data);
    }
  };

  const loadLeadSources = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    let query = supabase
      .from('realtors')
      .select('*');

    if (settingsView === 'personal') {
      // Load personal lead sources and global defaults
      query = query.or(`user_id.eq.${session.user.id},is_global.eq.true`);
    } else {
      // Load only global defaults
      query = query.eq('is_global', true);
    }

    const { data, error } = await query.order('display_order', { ascending: true });

    if (error) {
      console.error('Error loading lead sources:', error);
      return;
    }

    if (data) {
      setLeadSources(data);
    }
  };

  const loadDiscoveryFields = async () => {
    const { data, error } = await supabase
      .from('discovery_call_fields')
      .select('*')
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error loading discovery fields:', error);
      return;
    }

    if (data) {
      setDiscoveryFields(data);
    }
  };

  const loadQuickFilterConfigs = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    let query = supabase
      .from('quick_filter_configs')
      .select('*');

    if (settingsView === 'personal') {
      // Load personal quick filters and global defaults
      query = query.or(`user_id.eq.${session.user.id},is_global.eq.true`);
    } else {
      // Load only global defaults
      query = query.eq('is_global', true);
    }

    const { data, error } = await query.order('display_order', { ascending: true });

    if (error) {
      console.error('Error loading quick filter configs:', error);
      return;
    }

    // Always set the data from database, even if empty
    setQuickFilterConfigs(data || []);
  };

  const loadPreApprovalTemplate = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { data, error } = await supabase
      .from('pre_approval_templates')
      .select('*')
      .eq('user_id', session.user.id)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error loading pre-approval template:', error);
      return;
    }

    if (data) {
      setPreApprovalTemplate(data.template_content);
    } else {
      // Set default template
      setPreApprovalTemplate(`Dear {clientName},

I am pleased to inform you that you have been pre-approved for a mortgage loan with the following terms:

PRE-APPROVAL DETAILS:
• Loan Amount: {loanAmount}
• Purchase Price: {purchasePrice}
• Property State: {state}
• Loan Type: {loanType}

PRE-APPROVAL CONDITIONS:
This pre-approval is subject to the following conditions:
• Verification of employment and income
• Satisfactory property appraisal
• Clear title report
• No adverse changes to credit or financial status
• Final underwriting approval

VALIDITY PERIOD:
This pre-approval letter is valid for 90 days from the date of issuance.

NEXT STEPS:
With this pre-approval letter, you can confidently make offers on properties within your approved price range. Please contact me when you find a property you'd like to purchase, and we'll move forward with the full loan application process.

If you have any questions or need any clarification, please don't hesitate to reach out.

Best regards,
{brokerName}
{brokerEmail}`);
    }
  };

  const handleSavePreApprovalTemplate = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    // Check if template exists
    const { data: existingTemplate } = await supabase
      .from('pre_approval_templates')
      .select('id')
      .eq('user_id', session.user.id)
      .single();

    let error;
    if (existingTemplate) {
      // Update existing template
      const result = await supabase
        .from('pre_approval_templates')
        .update({
          template_content: preApprovalTemplate,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', session.user.id);
      error = result.error;
    } else {
      // Insert new template
      const result = await supabase
        .from('pre_approval_templates')
        .insert({
          template_content: preApprovalTemplate,
          user_id: session.user.id
        });
      error = result.error;
    }

    if (error) {
      console.error('Error saving pre-approval template:', error);
      alert('Failed to save pre-approval template');
      return;
    }

    setEditingPreApproval(false);
    setPreApprovalSaveMessage('Pre-approval template saved successfully!');
    
    // Clear message after 3 seconds
    setTimeout(() => {
      setPreApprovalSaveMessage('');
    }, 3000);
  };

  const formatPhoneNumber = (phone: string): string => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)})-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  // Comp Rates handlers
  const handleAddCompRate = async () => {
    if (newCompRate.broker && newCompRate.comp_percentage && newCompRate.personal_comp_percentage && newCompRate.full_broker_name) {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data, error } = await supabase
        .from('comp_rates')
        .insert([{
          user_id: session.user.id,
          broker: newCompRate.broker,
          comp_percentage: parseFloat(newCompRate.comp_percentage),
          personal_comp_percentage: parseFloat(newCompRate.personal_comp_percentage),
          full_broker_name: newCompRate.full_broker_name,
          email_address: newCompRate.email_address || null
        }])
        .select()
        .single();

      if (error) {
        console.error('Error adding comp rate:', error);
        alert('Failed to add comp rate');
        return;
      }

      if (data) {
        setCompRates([...compRates, data]);
      }

      setNewCompRate({ broker: '', comp_percentage: '', personal_comp_percentage: '', full_broker_name: '', email_address: '' });
      setShowAddCompForm(false);
    }
  };

  const handleSaveCompRate = async (id: string) => {
    const compRate = compRates.find(c => c.id === id);
    if (!compRate) return;

    const { error } = await supabase
      .from('comp_rates')
      .update({
        broker: compRate.broker,
        comp_percentage: compRate.comp_percentage,
        personal_comp_percentage: compRate.personal_comp_percentage,
        full_broker_name: compRate.full_broker_name,
        email_address: compRate.email_address || null
      })
      .eq('id', id);

    if (error) {
      console.error('Error updating comp rate:', error);
      alert('Failed to update comp rate');
      return;
    }

    setEditingCompId(null);
  };

  const handleDeleteCompRate = async (id: string) => {
    if (confirm('Are you sure you want to delete this comp rate?')) {
      const { error } = await supabase
        .from('comp_rates')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting comp rate:', error);
        alert('Failed to delete comp rate');
        return;
      }

      setCompRates(compRates.filter(c => c.id !== id));
    }
  };

  const handleMoveCompRateUp = async (id: string) => {
    const currentIndex = compRates.findIndex(r => r.id === id);
    if (currentIndex <= 0) return;

    const currentRate = compRates[currentIndex];
    const previousRate = compRates[currentIndex - 1];

    const newRates = [...compRates];
    [newRates[currentIndex - 1], newRates[currentIndex]] = [newRates[currentIndex], newRates[currentIndex - 1]];
    
    setCompRates(newRates);
  };

  const handleMoveCompRateDown = async (id: string) => {
    const currentIndex = compRates.findIndex(r => r.id === id);
    if (currentIndex >= compRates.length - 1) return;

    const currentRate = compRates[currentIndex];
    const nextRate = compRates[currentIndex + 1];

    const newRates = [...compRates];
    [newRates[currentIndex], newRates[currentIndex + 1]] = [newRates[currentIndex + 1], newRates[currentIndex]];
    
    setCompRates(newRates);
  };

  // Client States handlers
  const handleAddClientState = async () => {
    if (newClientState.state_name) {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Get max display order
      const { data: maxOrderData } = await supabase
        .from('client_states')
        .select('display_order')
        .order('display_order', { ascending: false })
        .limit(1);

      const maxOrder = maxOrderData && maxOrderData.length > 0 ? maxOrderData[0].display_order : 0;

      const insertData: any = {
        state_name: newClientState.state_name,
        display_order: maxOrder + 1
      };

      // If in personal view, add user_id and set is_global to false
      if (settingsView === 'personal') {
        insertData.user_id = session.user.id;
        insertData.is_global = false;
      } else {
        // If in global view, set is_global to true and user_id to null
        insertData.is_global = true;
        insertData.user_id = null;
      }

      const { data, error } = await supabase
        .from('client_states')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        console.error('Error adding client state:', error);
        alert('Failed to add client state');
        return;
      }

      if (data) {
        setClientStates([...clientStates, data]);
      }

      setNewClientState({ state_name: '', display_order: '' });
      setShowAddStateForm(false);
    }
  };

  const handleSaveClientState = async (id: string) => {
    const clientState = clientStates.find(c => c.id === id);
    if (!clientState) return;

    const { error } = await supabase
      .from('client_states')
      .update({
        state_name: clientState.state_name,
        display_order: clientState.display_order
      })
      .eq('id', id);

    if (error) {
      console.error('Error updating client state:', error);
      alert('Failed to update client state');
      return;
    }

    setEditingStateId(null);
  };

  const handleDeleteClientState = async (id: string) => {
    const stateToDelete = clientStates.find(s => s.id === id);
    if (!stateToDelete) return;

    // Prevent deletion of global defaults in personal view
    if (settingsView === 'personal' && stateToDelete.is_global) {
      alert('Cannot delete global defaults. Switch to Global Defaults view to manage them.');
      return;
    }

    if (confirm('Are you sure you want to delete this client state?')) {
      const { error } = await supabase
        .from('client_states')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting client state:', error);
        alert('Failed to delete client state');
        return;
      }

      setClientStates(clientStates.filter(c => c.id !== id));
    }
  };

  const handleMoveStateUp = async (id: string) => {
    const currentIndex = clientStates.findIndex(s => s.id === id);
    if (currentIndex <= 0) return; // Already at top

    const currentState = clientStates[currentIndex];
    const previousState = clientStates[currentIndex - 1];

    // Swap display orders
    const { error: error1 } = await supabase
      .from('client_states')
      .update({ display_order: previousState.display_order })
      .eq('id', currentState.id);

    const { error: error2 } = await supabase
      .from('client_states')
      .update({ display_order: currentState.display_order })
      .eq('id', previousState.id);

    if (error1 || error2) {
      console.error('Error moving state:', error1 || error2);
      alert('Failed to move state');
      return;
    }

    // Reload states
    loadClientStates();
  };

  const handleMoveStateDown = async (id: string) => {
    const currentIndex = clientStates.findIndex(s => s.id === id);
    if (currentIndex >= clientStates.length - 1) return; // Already at bottom

    const currentState = clientStates[currentIndex];
    const nextState = clientStates[currentIndex + 1];

    // Swap display orders
    const { error: error1 } = await supabase
      .from('client_states')
      .update({ display_order: nextState.display_order })
      .eq('id', currentState.id);

    const { error: error2 } = await supabase
      .from('client_states')
      .update({ display_order: currentState.display_order })
      .eq('id', nextState.id);

    if (error1 || error2) {
      console.error('Error moving state:', error1 || error2);
      alert('Failed to move state');
      return;
    }

    // Reload states
    loadClientStates();
  };

  const handleCopyGlobalStateToPersonal = async (globalStateId: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const globalState = clientStates.find(s => s.id === globalStateId);
    if (!globalState) return;

    // Check if user already has this state
    const { data: existingState } = await supabase
      .from('client_states')
      .select('id')
      .eq('user_id', session.user.id)
      .eq('state_name', globalState.state_name)
      .maybeSingle();

    if (existingState) {
      alert('You already have a personal copy of this state');
      return;
    }

    // Get max display order for user's personal states
    const { data: maxOrderData } = await supabase
      .from('client_states')
      .select('display_order')
      .eq('user_id', session.user.id)
      .order('display_order', { ascending: false })
      .limit(1);

    const maxOrder = maxOrderData && maxOrderData.length > 0 ? maxOrderData[0].display_order : 0;

    // Create personal copy
    const { data, error } = await supabase
      .from('client_states')
      .insert([{
        state_name: globalState.state_name,
        display_order: maxOrder + 1,
        user_id: session.user.id,
        is_global: false
      }])
      .select()
      .single();

    if (error) {
      console.error('Error copying state:', error);
      alert('Failed to copy state to personal settings');
      return;
    }

    if (data) {
      setClientStates([...clientStates, data]);
      alert('State copied to your personal settings!');
    }
  };

  // Lender Names handlers
  const handleAddLenderName = async () => {
    if (newLenderName.lender_name) {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Get max display order
      const { data: maxOrderData } = await supabase
        .from('lender_names')
        .select('display_order')
        .order('display_order', { ascending: false })
        .limit(1);

      const maxOrder = maxOrderData && maxOrderData.length > 0 ? maxOrderData[0].display_order : 0;

      const insertData: any = {
        lender_name: newLenderName.lender_name,
        display_order: maxOrder + 1
      };

      // If in personal view, add user_id and set is_global to false
      if (settingsView === 'personal') {
        insertData.user_id = session.user.id;
        insertData.is_global = false;
      } else {
        // If in global view, set is_global to true and user_id to null
        insertData.is_global = true;
        insertData.user_id = null;
      }

      const { data, error } = await supabase
        .from('lender_names')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        console.error('Error adding lender name:', error);
        alert('Failed to add lender name');
        return;
      }

      if (data) {
        setLenderNames([...lenderNames, data]);
      }

      setNewLenderName({ lender_name: '', display_order: '' });
      setShowAddLenderForm(false);
    }
  };

  const handleSaveLenderName = async (id: string) => {
    const lenderName = lenderNames.find(l => l.id === id);
    if (!lenderName) return;

    const { error } = await supabase
      .from('lender_names')
      .update({
        lender_name: lenderName.lender_name,
        display_order: lenderName.display_order
      })
      .eq('id', id);

    if (error) {
      console.error('Error updating lender name:', error);
      alert('Failed to update lender name');
      return;
    }

    setEditingLenderId(null);
  };

  const handleDeleteLenderName = async (id: string) => {
    const lenderToDelete = lenderNames.find(l => l.id === id);
    if (!lenderToDelete) return;

    // Prevent deletion of global defaults in personal view
    if (settingsView === 'personal' && lenderToDelete.is_global) {
      alert('Cannot delete global defaults. Switch to Global Defaults view to manage them.');
      return;
    }

    if (confirm('Are you sure you want to delete this lender name?')) {
      const { error } = await supabase
        .from('lender_names')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting lender name:', error);
        alert('Failed to delete lender name');
        return;
      }

      setLenderNames(lenderNames.filter(l => l.id !== id));
    }
  };

  const handleMoveLenderUp = async (id: string) => {
    const currentIndex = lenderNames.findIndex(l => l.id === id);
    if (currentIndex <= 0) return;

    const currentLender = lenderNames[currentIndex];
    const previousLender = lenderNames[currentIndex - 1];

    const { error: error1 } = await supabase
      .from('lender_names')
      .update({ display_order: previousLender.display_order })
      .eq('id', currentLender.id);

    const { error: error2 } = await supabase
      .from('lender_names')
      .update({ display_order: currentLender.display_order })
      .eq('id', previousLender.id);

    if (error1 || error2) {
      console.error('Error moving lender:', error1 || error2);
      alert('Failed to move lender');
      return;
    }

    loadLenderNames();
  };

  const handleMoveLenderDown = async (id: string) => {
    const currentIndex = lenderNames.findIndex(l => l.id === id);
    if (currentIndex >= lenderNames.length - 1) return;

    const currentLender = lenderNames[currentIndex];
    const nextLender = lenderNames[currentIndex + 1];

    const { error: error1 } = await supabase
      .from('lender_names')
      .update({ display_order: nextLender.display_order })
      .eq('id', currentLender.id);

    const { error: error2 } = await supabase
      .from('lender_names')
      .update({ display_order: currentLender.display_order })
      .eq('id', nextLender.id);

    if (error1 || error2) {
      console.error('Error moving lender:', error1 || error2);
      alert('Failed to move lender');
      return;
    }

    loadLenderNames();
  };

  const handleCopyGlobalLenderToPersonal = async (globalLenderId: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const globalLender = lenderNames.find(l => l.id === globalLenderId);
    if (!globalLender) return;

    // Check if user already has this lender
    const { data: existingLender } = await supabase
      .from('lender_names')
      .select('id')
      .eq('user_id', session.user.id)
      .eq('lender_name', globalLender.lender_name)
      .maybeSingle();

    if (existingLender) {
      alert('You already have a personal copy of this lender');
      return;
    }

    // Get max display order for user's personal lenders
    const { data: maxOrderData } = await supabase
      .from('lender_names')
      .select('display_order')
      .eq('user_id', session.user.id)
      .order('display_order', { ascending: false })
      .limit(1);

    const maxOrder = maxOrderData && maxOrderData.length > 0 ? maxOrderData[0].display_order : 0;

    // Create personal copy
    const { data, error } = await supabase
      .from('lender_names')
      .insert([{
        lender_name: globalLender.lender_name,
        display_order: maxOrder + 1,
        user_id: session.user.id,
        is_global: false
      }])
      .select()
      .single();

    if (error) {
      console.error('Error copying lender:', error);
      alert('Failed to copy lender to personal settings');
      return;
    }

    if (data) {
      setLenderNames([...lenderNames, data]);
      alert('Lender copied to your personal settings!');
    }
  };

  // Loan Types handlers
  const handleAddLoanType = async () => {
    if (newLoanType.loan_type) {
      // Get max display order
      const { data: maxOrderData } = await supabase
        .from('loan_types')
        .select('display_order')
        .order('display_order', { ascending: false })
        .limit(1);

      const maxOrder = maxOrderData && maxOrderData.length > 0 ? maxOrderData[0].display_order : 0;

      const { data, error } = await supabase
        .from('loan_types')
        .insert([{
          loan_type: newLoanType.loan_type,
          display_order: maxOrder + 1,
          intake_mapping: newLoanType.intake_mapping
        }])
        .select()
        .single();

      if (error) {
        console.error('Error adding loan type:', error);
        alert('Failed to add loan type');
        return;
      }

      if (data) {
        setLoanTypes([...loanTypes, data]);
      }

      setNewLoanType({ loan_type: '', display_order: '', intake_mapping: '' });
      setShowAddLoanTypeForm(false);
    }
  };

  const handleSaveLoanType = async (id: string) => {
    const loanType = loanTypes.find(l => l.id === id);
    if (!loanType) return;

    const { error } = await supabase
      .from('loan_types')
      .update({
        loan_type: loanType.loan_type,
        display_order: loanType.display_order,
        intake_mapping: loanType.intake_mapping
      })
      .eq('id', id);

    if (error) {
      console.error('Error updating loan type:', error);
      alert('Failed to update loan type');
      return;
    }

    setEditingLoanTypeId(null);
  };

  const handleDeleteLoanType = async (id: string) => {
    if (confirm('Are you sure you want to delete this loan type?')) {
      const { error } = await supabase
        .from('loan_types')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting loan type:', error);
        alert('Failed to delete loan type');
        return;
      }

      setLoanTypes(loanTypes.filter(l => l.id !== id));
    }
  };

  const handleMoveLoanTypeUp = async (id: string) => {
    const currentIndex = loanTypes.findIndex(l => l.id === id);
    if (currentIndex <= 0) return;

    const currentType = loanTypes[currentIndex];
    const previousType = loanTypes[currentIndex - 1];

    const { error: error1 } = await supabase
      .from('loan_types')
      .update({ display_order: previousType.display_order })
      .eq('id', currentType.id);

    const { error: error2 } = await supabase
      .from('loan_types')
      .update({ display_order: currentType.display_order })
      .eq('id', previousType.id);

    if (error1 || error2) {
      console.error('Error moving loan type:', error1 || error2);
      alert('Failed to move loan type');
      return;
    }

    loadLoanTypes();
  };

  const handleMoveLoanTypeDown = async (id: string) => {
    const currentIndex = loanTypes.findIndex(l => l.id === id);
    if (currentIndex >= loanTypes.length - 1) return;

    const currentType = loanTypes[currentIndex];
    const nextType = loanTypes[currentIndex + 1];

    const { error: error1 } = await supabase
      .from('loan_types')
      .update({ display_order: nextType.display_order })
      .eq('id', currentType.id);

    const { error: error2 } = await supabase
      .from('loan_types')
      .update({ display_order: currentType.display_order })
      .eq('id', nextType.id);

    if (error1 || error2) {
      console.error('Error moving loan type:', error1 || error2);
      alert('Failed to move loan type');
      return;
    }

    loadLoanTypes();
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  // Lead Source handlers (renamed from Realtors)
  const handleAddLeadSource = async () => {
    if (newLeadSource.name) {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Get max display order
      const { data: maxOrderData } = await supabase
        .from('realtors')
        .select('display_order')
        .order('display_order', { ascending: false })
        .limit(1);

      const maxOrder = maxOrderData && maxOrderData.length > 0 ? maxOrderData[0].display_order : 0;

      const insertData: any = {
        name: newLeadSource.name,
        email: newLeadSource.email || '',
        phone: newLeadSource.phone || '',
        state: newLeadSource.state || '',
        display_order: maxOrder + 1
      };

      // If in personal view, add user_id and set is_global to false
      if (settingsView === 'personal') {
        insertData.user_id = session.user.id;
        insertData.is_global = false;
      } else {
        // If in global view, set is_global to true and user_id to null
        insertData.is_global = true;
        insertData.user_id = null;
      }

      const { data, error } = await supabase
        .from('realtors')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        console.error('Error adding lead source:', error);
        alert('Failed to add lead source');
        return;
      }

      if (data) {
        setLeadSources([...leadSources, data]);
      }

      setNewLeadSource({ name: '', email: '', phone: '', state: '', display_order: '' });
      setShowAddLeadSourceForm(false);
    } else {
      alert('Please fill in the required field: Name');
    }
  };

  const handleSaveLeadSource = async (id: string) => {
    const leadSource = leadSources.find(r => r.id === id);
    if (!leadSource) return;

    const { error } = await supabase
      .from('realtors')
      .update({
        name: leadSource.name,
        email: leadSource.email,
        phone: leadSource.phone,
        state: leadSource.state,
        display_order: leadSource.display_order
      })
      .eq('id', id);

    if (error) {
      console.error('Error updating lead source:', error);
      alert('Failed to update lead source');
      return;
    }

    setEditingLeadSourceId(null);
  };

  const handleDeleteLeadSource = async (id: string) => {
    const leadSourceToDelete = leadSources.find(l => l.id === id);
    if (!leadSourceToDelete) return;

    // Prevent deletion of global defaults in personal view (unless admin in global view)
    if (settingsView === 'personal' && leadSourceToDelete.is_global) {
      alert('Cannot delete global defaults. Switch to Global Defaults view to manage them.');
      return;
    }

    if (confirm('Are you sure you want to delete this lead source?')) {
      const { error } = await supabase
        .from('realtors')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting lead source:', error);
        alert('Failed to delete lead source');
        return;
      }

      setLeadSources(leadSources.filter(r => r.id !== id));
    }
  };

  const handleMoveLeadSourceUp = async (id: string) => {
    const currentIndex = leadSources.findIndex(r => r.id === id);
    if (currentIndex <= 0) return;

    const currentLeadSource = leadSources[currentIndex];
    const previousLeadSource = leadSources[currentIndex - 1];

    const { error: error1 } = await supabase
      .from('realtors')
      .update({ display_order: previousLeadSource.display_order })
      .eq('id', currentLeadSource.id);

    const { error: error2 } = await supabase
      .from('realtors')
      .update({ display_order: currentLeadSource.display_order })
      .eq('id', previousLeadSource.id);

    if (error1 || error2) {
      console.error('Error moving lead source:', error1 || error2);
      alert('Failed to move lead source');
      return;
    }

    loadLeadSources();
  };

  const handleMoveLeadSourceDown = async (id: string) => {
    const currentIndex = leadSources.findIndex(r => r.id === id);
    if (currentIndex >= leadSources.length - 1) return;

    const currentLeadSource = leadSources[currentIndex];
    const nextLeadSource = leadSources[currentIndex + 1];

    const { error: error1 } = await supabase
      .from('realtors')
      .update({ display_order: nextLeadSource.display_order })
      .eq('id', currentLeadSource.id);

    const { error: error2 } = await supabase
      .from('realtors')
      .update({ display_order: currentLeadSource.display_order })
      .eq('id', nextLeadSource.id);

    if (error1 || error2) {
      console.error('Error moving lead source:', error1 || error2);
      alert('Failed to move lead source');
      return;
    }

    loadLeadSources();
  };

  const handleCopyGlobalLeadSourceToPersonal = async (globalLeadSourceId: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const globalLeadSource = leadSources.find(l => l.id === globalLeadSourceId);
    if (!globalLeadSource) return;

    // Check if user already has this lead source
    const { data: existingLeadSource } = await supabase
      .from('realtors')
      .select('id')
      .eq('user_id', session.user.id)
      .eq('name', globalLeadSource.name)
      .maybeSingle();

    if (existingLeadSource) {
      alert('You already have a personal copy of this lead source');
      return;
    }

    // Get max display order for user's personal lead sources
    const { data: maxOrderData } = await supabase
      .from('realtors')
      .select('display_order')
      .eq('user_id', session.user.id)
      .order('display_order', { ascending: false })
      .limit(1);

    const maxOrder = maxOrderData && maxOrderData.length > 0 ? maxOrderData[0].display_order : 0;

    // Create personal copy
    const { data, error } = await supabase
      .from('realtors')
      .insert([{
        name: globalLeadSource.name,
        email: globalLeadSource.email,
        phone: globalLeadSource.phone,
        state: globalLeadSource.state,
        display_order: maxOrder + 1,
        user_id: session.user.id,
        is_global: false
      }])
      .select()
      .single();

    if (error) {
      console.error('Error copying lead source:', error);
      alert('Failed to copy lead source to personal settings');
      return;
    }

    if (data) {
      setLeadSources([...leadSources, data]);
      alert('Lead source copied to your personal settings!');
    }
  };

  const handleCopyGlobalQuickFilterToPersonal = async (globalFilterId: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const globalFilter = quickFilterConfigs.find(f => f.id === globalFilterId);
    if (!globalFilter) return;

    // Check if user already has a filter with this name
    const { data: existingFilter } = await supabase
      .from('quick_filter_configs')
      .select('id')
      .eq('user_id', session.user.id)
      .eq('filter_name', globalFilter.filter_name)
      .maybeSingle();

    if (existingFilter) {
      alert('You already have a personal copy of this quick filter');
      return;
    }

    // Get max display order for user's personal filters
    const { data: maxOrderData } = await supabase
      .from('quick_filter_configs')
      .select('display_order')
      .eq('user_id', session.user.id)
      .order('display_order', { ascending: false })
      .limit(1);

    const maxOrder = maxOrderData && maxOrderData.length > 0 ? maxOrderData[0].display_order : 0;

    // Generate a unique ID for the personal copy
    const newId = `${globalFilter.filter_name.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`;

    // Create personal copy
    const { data, error } = await supabase
      .from('quick_filter_configs')
      .insert([{
        id: newId,
        filter_name: globalFilter.filter_name,
        client_states: globalFilter.client_states,
        display_order: maxOrder + 1,
        user_id: session.user.id,
        is_global: false
      }])
      .select()
      .single();

    if (error) {
      console.error('Error copying quick filter:', error);
      alert('Failed to copy quick filter to personal settings');
      return;
    }

    if (data) {
      setQuickFilterConfigs([...quickFilterConfigs, data]);
      alert('Quick filter copied to your personal settings!');
    }
  };

  // Discovery Call Fields handlers
  const handleAddDiscoveryField = async () => {
    if (newDiscoveryField.field_name && newDiscoveryField.field_label) {
      // Get max display order
      const { data: maxOrderData } = await supabase
        .from('discovery_call_fields')
        .select('display_order')
        .order('display_order', { ascending: false })
        .limit(1);

      const maxOrder = maxOrderData && maxOrderData.length > 0 ? maxOrderData[0].display_order : 0;

      const dropdownOptions = newDiscoveryField.field_type === 'select' && newDiscoveryField.dropdown_options
        ? newDiscoveryField.dropdown_options.split(',').map(opt => opt.trim()).filter(opt => opt)
        : null;

      const { data, error } = await supabase
        .from('discovery_call_fields')
        .insert([{
          field_name: newDiscoveryField.field_name,
          field_label: newDiscoveryField.field_label,
          field_type: newDiscoveryField.field_type,
          dropdown_options: dropdownOptions,
          display_order: maxOrder + 1,
          is_required: newDiscoveryField.is_required
        }])
        .select()
        .single();

      if (error) {
        console.error('Error adding discovery field:', error);
        alert('Failed to add discovery field');
        return;
      }

      if (data) {
        setDiscoveryFields([...discoveryFields, data]);
      }

      setNewDiscoveryField({ field_name: '', field_label: '', field_type: 'text', dropdown_options: '', is_required: false });
      setShowAddDiscoveryFieldForm(false);
    }
  };

  const handleSaveDiscoveryField = async (id: string) => {
    const field = discoveryFields.find(f => f.id === id);
    if (!field) return;

    const { error } = await supabase
      .from('discovery_call_fields')
      .update({
        field_name: field.field_name,
        field_label: field.field_label,
        field_type: field.field_type,
        dropdown_options: field.dropdown_options,
        display_order: field.display_order,
        is_required: field.is_required
      })
      .eq('id', id);

    if (error) {
      console.error('Error updating discovery field:', error);
      alert('Failed to update discovery field');
      return;
    }

    setEditingDiscoveryFieldId(null);
  };

  const handleDeleteDiscoveryField = async (id: string) => {
    if (confirm('Are you sure you want to delete this discovery field?')) {
      const { error } = await supabase
        .from('discovery_call_fields')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting discovery field:', error);
        alert('Failed to delete discovery field');
        return;
      }

      setDiscoveryFields(discoveryFields.filter(f => f.id !== id));
    }
  };

  const handleMoveDiscoveryFieldUp = async (id: string) => {
    const currentIndex = discoveryFields.findIndex(f => f.id === id);
    if (currentIndex <= 0) return;

    const currentField = discoveryFields[currentIndex];
    const previousField = discoveryFields[currentIndex - 1];

    const { error: error1 } = await supabase
      .from('discovery_call_fields')
      .update({ display_order: previousField.display_order })
      .eq('id', currentField.id);

    const { error: error2 } = await supabase
      .from('discovery_call_fields')
      .update({ display_order: currentField.display_order })
      .eq('id', previousField.id);

    if (error1 || error2) {
      console.error('Error moving discovery field:', error1 || error2);
      alert('Failed to move discovery field');
      return;
    }

    loadDiscoveryFields();
  };

  const handleMoveDiscoveryFieldDown = async (id: string) => {
    const currentIndex = discoveryFields.findIndex(f => f.id === id);
    if (currentIndex >= discoveryFields.length - 1) return;

    const currentField = discoveryFields[currentIndex];
    const nextField = discoveryFields[currentIndex + 1];

    const { error: error1 } = await supabase
      .from('discovery_call_fields')
      .update({ display_order: nextField.display_order })
      .eq('id', currentField.id);

    const { error: error2 } = await supabase
      .from('discovery_call_fields')
      .update({ display_order: currentField.display_order })
      .eq('id', nextField.id);

    if (error1 || error2) {
      console.error('Error moving discovery field:', error1 || error2);
      alert('Failed to move discovery field');
      return;
    }

    loadDiscoveryFields();
  };

  // Quick Filter Config handlers
  const handleSaveQuickFilterConfig = async (id: string) => {
    const config = quickFilterConfigs.find(c => c.id === id);
    if (!config) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { error } = await supabase
      .from('quick_filter_configs')
      .upsert({
        id: config.id,
        filter_name: config.filter_name,
        client_states: config.client_states,
        display_order: config.display_order,
        user_id: config.user_id,
        is_global: config.is_global
      }, {
        onConflict: 'id'
      });

    if (error) {
      console.error('Error updating quick filter config:', error);
      alert('Failed to update quick filter configuration');
      return;
    }

    setEditingQuickFilterId(null);
    
    // Show success message
    const successMessage = document.createElement('div');
    successMessage.className = 'fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50';
    successMessage.innerHTML = '<i class="ri-check-line mr-2"></i>Quick filter configuration saved successfully!';
    document.body.appendChild(successMessage);
    
    setTimeout(() => {
      if (document.body.contains(successMessage)) {
        document.body.removeChild(successMessage);
      }
    }, 3000);
  };

  const handleMoveQuickFilterUp = async (id: string) => {
    const currentIndex = quickFilterConfigs.findIndex(c => c.id === id);
    if (currentIndex <= 0) return;

    const currentConfig = quickFilterConfigs[currentIndex];
    const previousConfig = quickFilterConfigs[currentIndex - 1];

    // Swap display orders
    const { error: error1 } = await supabase
      .from('quick_filter_configs')
      .update({ display_order: previousConfig.display_order })
      .eq('id', currentConfig.id);

    const { error: error2 } = await supabase
      .from('quick_filter_configs')
      .update({ display_order: currentConfig.display_order })
      .eq('id', previousConfig.id);

    if (error1 || error2) {
      console.error('Error moving quick filter:', error1 || error2);
      alert('Failed to move quick filter');
      return;
    }

    // Reload quick filter configs
    loadQuickFilterConfigs();
  };

  const handleMoveQuickFilterDown = async (id: string) => {
    const currentIndex = quickFilterConfigs.findIndex(c => c.id === id);
    if (currentIndex >= quickFilterConfigs.length - 1) return;

    const currentConfig = quickFilterConfigs[currentIndex];
    const nextConfig = quickFilterConfigs[currentIndex + 1];

    // Swap display orders
    const { error: error1 } = await supabase
      .from('quick_filter_configs')
      .update({ display_order: nextConfig.display_order })
      .eq('id', currentConfig.id);

    const { error: error2 } = await supabase
      .from('quick_filter_configs')
      .update({ display_order: currentConfig.display_order })
      .eq('id', nextConfig.id);

    if (error1 || error2) {
      console.error('Error moving quick filter:', error1 || error2);
      alert('Failed to move quick filter');
      return;
    }

    // Reload quick filter configs
    loadQuickFilterConfigs();
  };

  const handleAddQuickFilter = async () => {
    if (!newQuickFilterName.trim()) {
      alert('Please enter a filter name');
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    // Generate a unique ID based on the filter name and timestamp
    const newId = `${newQuickFilterName.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`;

    // Get max display order
    const { data: maxOrderData } = await supabase
      .from('quick_filter_configs')
      .select('display_order')
      .order('display_order', { ascending: false })
      .limit(1);

    const maxOrder = maxOrderData && maxOrderData.length > 0 ? maxOrderData[0].display_order : 0;

    const newConfig: QuickFilterConfig = {
      id: newId,
      filter_name: newQuickFilterName.trim(),
      client_states: [],
      display_order: maxOrder + 1,
      user_id: settingsView === 'personal' ? session.user.id : null,
      is_global: settingsView === 'global'
    };

    const { error } = await supabase
      .from('quick_filter_configs')
      .insert(newConfig);

    if (error) {
      console.error('Error adding quick filter:', error);
      alert('Failed to add quick filter');
      return;
    }

    setQuickFilterConfigs([...quickFilterConfigs, newConfig]);
    setNewQuickFilterName('');
    setShowAddQuickFilterForm(false);
    alert('Quick filter added successfully!');
  };

  const handleDeleteQuickFilter = async (id: string) => {
    const filterToDelete = quickFilterConfigs.find(c => c.id === id);
    if (!filterToDelete) return;

    // Prevent deletion of global defaults in personal view
    if (settingsView === 'personal' && filterToDelete.is_global) {
      alert('Cannot delete global defaults. Switch to Global Defaults view to manage them.');
      return;
    }

    if (!confirm('Are you sure you want to delete this quick filter?')) return;

    // Delete from database
    const { error } = await supabase
      .from('quick_filter_configs')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting quick filter:', error);
      alert('Failed to delete quick filter. Please try again.');
      return;
    }

    // Update local state only after successful deletion
    setQuickFilterConfigs(prevConfigs => prevConfigs.filter(c => c.id !== id));
    
    // Show success message
    const successMessage = document.createElement('div');
    successMessage.className = 'fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50';
    successMessage.innerHTML = '<i class="ri-check-line mr-2"></i>Quick filter deleted successfully!';
    document.body.appendChild(successMessage);
    
    setTimeout(() => {
      if (document.body.contains(successMessage)) {
        document.body.removeChild(successMessage);
      }
    }, 3000);
  };

  const handleUpdateFilterName = async (id: string, newName: string) => {
    if (!newName.trim()) {
      alert('Filter name cannot be empty');
      return;
    }

    const { error } = await supabase
      .from('quick_filter_configs')
      .update({ filter_name: newName.trim() })
      .eq('id', id);

    if (error) {
      console.error('Error updating filter name:', error);
      alert('Failed to update filter name');
      return;
    }

    setQuickFilterConfigs(quickFilterConfigs.map(c => 
      c.id === id ? { ...c, filter_name: newName.trim() } : c
    ));
  };

  const handleAddClientStateToFilter = (filterId: string, stateName: string) => {
    setQuickFilterConfigs(quickFilterConfigs.map(config => {
      if (config.id === filterId) {
        if (!config.client_states.includes(stateName)) {
          return {
            ...config,
            client_states: [...config.client_states, stateName]
          };
        }
      }
      return config;
    }));
  };

  const handleRemoveClientStateFromFilter = (filterId: string, stateName: string) => {
    setQuickFilterConfigs(quickFilterConfigs.map(config => {
      if (config.id === filterId) {
        return {
          ...config,
          client_states: config.client_states.filter(s => s !== stateName)
        };
      }
      return config;
    }));
  };

  const handleSaveProfile = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        alert('Failed to get user information');
        return;
      }

      // Update user metadata
      const { error: updateError } = await supabase.auth.updateUser({
        data: {
          first_name: userProfile.firstName,
          last_name: userProfile.lastName,
          full_name: `${userProfile.firstName} ${userProfile.lastName}`,
          phone: userProfile.phone,
          nmls: userProfile.nmls
        }
      });

      if (updateError) {
        console.error('Error updating profile:', updateError);
        alert('Failed to update profile');
        return;
      }

      // Update email if changed
      if (userProfile.email !== user.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: userProfile.email
        });

        if (emailError) {
          console.error('Error updating email:', emailError);
          alert('Failed to update email. You may need to verify the new email address.');
          return;
        }
        
        setProfileSaveMessage('Profile updated! Please check your new email address to verify the change.');
      } else {
        setProfileSaveMessage('Profile updated successfully!');
      }

      setEditingProfile(false);
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setProfileSaveMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to save profile');
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-black' : 'bg-gray-50'}`}>
      {/* Navigation */}
      <nav className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm border-b`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center">
              <i className={`ri-home-heart-line text-3xl ${isDarkMode ? 'text-teal' : 'text-navy'}`}></i>
            </div>
            <span className={`text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-navy'}`}>{userProfile.firstName && userProfile.lastName ? `${userProfile.firstName} ${userProfile.lastName}` : 'Loans with Heena'}</span>
          </div>
          
          <div className="hidden md:flex items-center gap-4">
            <a href="/" className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer`}>
              <i className="ri-home-5-line mr-2"></i>
              Home
            </a>
            
            {/* Pages Dropdown */}
            <div className="relative group">
              <button className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-navy'} transition-colors font-medium whitespace-nowrap cursor-pointer flex items-center gap-1`}>
                <i className="ri-pages-line mr-2"></i>
                Pages
                <i className="ri-arrow-down-s-line"></i>
              </button>
              <div className={`absolute top-full left-0 mt-1 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-lg shadow-lg py-2 min-w-[200px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50`}>
                <a href="/crm" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-contacts-book-2-line mr-2"></i>
                  CRM
                </a>
                <a href="/closed" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-archive-line mr-2"></i>
                  Closed
                </a>
                <a href="/dead-deals" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-close-circle-line mr-2"></i>
                  Dead Deals
                </a>
                <a href="/on-hold" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-pause-circle-line mr-2"></i>
                  On Hold
                </a>
                <a href="/templates" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-file-text-line mr-2"></i>
                  Templates
                </a>
                <a href="/intake" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-inbox-line mr-2"></i>
                  Message Intake
                </a>
                <a href="/history" className={`block px-4 py-2 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700 hover:text-gray-100' : 'text-gray-600 hover:bg-gray-50 hover:text-navy'} transition-colors cursor-pointer`}>
                  <i className="ri-history-line mr-2"></i>
                  History
                </a>
                <a href="/admin" className={`block px-4 py-2 ${isDarkMode ? 'text-teal bg-teal/10' : 'text-navy bg-gray-50'} font-semibold cursor-pointer`}>
                  <i className="ri-settings-3-line mr-2"></i>
                  Admin
                </a>
              </div>
            </div>
            
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className={`${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-900'} transition-colors font-medium whitespace-nowrap cursor-pointer`}
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              <i className={`${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'} text-xl`}></i>
            </button>
            
            <button
              onClick={handleLogout}
              className="text-red-600 hover:text-red-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
            >
              <i className="ri-logout-box-line mr-2"></i>
              Logout
            </button>
          </div>
          
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-900'} cursor-pointer`}
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`md:hidden border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} rounded-lg shadow-lg py-2 min-w-[200px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50`}
          >
            <div className="px-4 py-2 space-y-1">
              <a href="/" className={`block px-4 py-3 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'} rounded-lg cursor-pointer`}>
                <i className="ri-home-5-line mr-3"></i>
                Home
              </a>
              <a href="/crm" className={`block px-4 py-3 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'} rounded-lg cursor-pointer`}>
                <i className="ri-contacts-book-2-line mr-3"></i>
                CRM
              </a>
              <a href="/closed" className={`block px-4 py-3 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'} rounded-lg cursor-pointer`}>
                <i className="ri-archive-line mr-3"></i>
                Closed
              </a>
              <a href="/dead-deals" className={`block px-4 py-3 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'} rounded-lg cursor-pointer`}>
                <i className="ri-close-circle-line mr-3"></i>
                Dead Deals
              </a>
              <a href="/on-hold" className={`block px-4 py-3 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'} rounded-lg cursor-pointer`}>
                <i className="ri-pause-circle-line mr-3"></i>
                On Hold
              </a>
              <a href="/templates" className={`block px-4 py-3 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'} rounded-lg cursor-pointer`}>
                <i className="ri-file-text-line mr-3"></i>
                Templates
              </a>
              <a href="/intake" className={`block px-4 py-3 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'} rounded-lg cursor-pointer font-medium`}>
                  <i className="ri-mail-line mr-3"></i>
                  Message Intake
                </a>
              <a href="/history" className={`block px-4 py-3 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'} rounded-lg cursor-pointer`}>
                <i className="ri-history-line mr-3"></i>
                History
              </a>
              <a href="/admin" className={`block px-4 py-3 ${isDarkMode ? 'text-teal bg-teal/10' : 'text-navy bg-navy/10'} font-semibold cursor-pointer`}>
                <i className="ri-settings-3-line mr-3"></i>
                Admin
              </a>
              <button
                onClick={toggleDarkMode}
                className={`w-full text-left px-4 py-3 ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'} rounded-lg cursor-pointer`}
              >
                <i className={`${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'} mr-3`}></i>
                {isDarkMode ? 'Light Mode' : 'Dark Mode'}
              </button>
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg cursor-pointer"
              >
                <i className="ri-logout-box-line mr-3"></i>
                Logout
              </button>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'} mb-2`}>Admin Settings</h1>
          <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Manage your profile, compensation rates, client states, lender names, and loan types</p>
        </div>

        {/* Settings View Toggle */}
        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6 mb-6`}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className={`text-lg font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'} mb-1`}>Settings View</h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {settingsView === 'personal' 
                  ? 'Viewing your personal settings and global defaults' 
                  : 'Viewing and managing global defaults for all users'}
              </p>
            </div>
            <div className={`flex items-center ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-lg p-1`}>
              <button
                onClick={() => setSettingsView('personal')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  settingsView === 'personal'
                    ? `${isDarkMode ? 'bg-teal text-white' : 'bg-teal text-white'} shadow-sm`
                    : `${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-900'}`
                }`}
              >
                <i className="ri-user-line mr-2"></i>
                My Settings
              </button>
              {isAdmin && (
                <button
                  onClick={() => setSettingsView('global')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    settingsView === 'global'
                      ? `${isDarkMode ? 'bg-teal text-white' : 'bg-teal text-white'} shadow-sm`
                      : `${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-900'}`
                  }`}
                >
                  <i className="ri-global-line mr-2"></i>
                  Global Defaults
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Profile Settings Section */}
        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6 mb-6`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Profile Settings</h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Update your personal information</p>
            </div>
            {!editingProfile && (
              <button
                onClick={() => setEditingProfile(true)}
                className="bg-teal text-white px-4 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-edit-line mr-2"></i>
                Edit Profile
              </button>
            )}
          </div>

          {profileSaveMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg text-sm"
            >
              <i className="ri-check-line mr-2"></i>
              {profileSaveMessage}
            </motion.div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-2`}>
                First Name
              </label>
              {editingProfile ? (
                <input
                  type="text"
                  value={userProfile.firstName}
                  onChange={(e) => setUserProfile({ ...userProfile, firstName: e.target.value })}
                  className={`w-full px-3 py-2 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                  placeholder="Enter your first name"
                />
              ) : (
                <div className={`px-3 py-2 ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-50 text-gray-900'} rounded-lg text-sm`}>
                  {userProfile.firstName || 'Not set'}
                </div>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-2`}>
                Last Name
              </label>
              {editingProfile ? (
                <input
                  type="text"
                  value={userProfile.lastName}
                  onChange={(e) => setUserProfile({ ...userProfile, lastName: e.target.value })}
                  className={`w-full px-3 py-2 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                  placeholder="Enter your last name"
                />
              ) : (
                <div className={`px-3 py-2 ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-50 text-gray-900'} rounded-lg text-sm`}>
                  {userProfile.lastName || 'Not set'}
                </div>
              )}
            </div>

            <div className="md:col-span-2">
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-2`}>
                Email Address
              </label>
              {editingProfile ? (
                <input
                  type="email"
                  value={userProfile.email}
                  onChange={(e) => setUserProfile({ ...userProfile, email: e.target.value })}
                  className={`w-full px-3 py-2 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                  placeholder="Enter your email address"
                />
              ) : (
                <div className={`px-3 py-2 ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-50 text-gray-900'} rounded-lg text-sm`}>
                  {userProfile.email || 'Not set'}
                </div>
              )}
              {editingProfile && (
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                  Changing your email will require verification of the new address
                </p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-2`}>
                Phone Number
              </label>
              {editingProfile ? (
                <input
                  type="tel"
                  value={userProfile.phone}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '');
                    if (digits.length <= 10) {
                      let formattedValue = digits;
                      if (digits.length >= 6) {
                        formattedValue = `(${digits.slice(0, 3)})-${digits.slice(3, 6)}-${digits.slice(6)}`;
                      } else if (digits.length >= 3) {
                        formattedValue = `(${digits.slice(0, 3)})-${digits.slice(3)}`;
                      }
                      setUserProfile({ ...userProfile, phone: formattedValue });
                    }
                  }}
                  placeholder="(###)-###-####"
                  className={`w-full px-3 py-2 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                />
              ) : (
                <div className={`px-3 py-2 ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-50 text-gray-900'} rounded-lg text-sm`}>
                  {userProfile.phone ? formatPhoneNumber(userProfile.phone) : 'Not set'}
                </div>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-2`}>
                NMLS#
              </label>
              {editingProfile ? (
                <input
                  type="text"
                  value={userProfile.nmls}
                  onChange={(e) => setUserProfile({ ...userProfile, nmls: e.target.value })}
                  className={`w-full px-3 py-2 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                  placeholder="Enter your NMLS number"
                />
              ) : (
                <div className={`px-3 py-2 ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-50 text-gray-900'} rounded-lg text-sm`}>
                  {userProfile.nmls || 'Not set'}
                </div>
              )}
            </div>
          </div>

          {editingProfile && (
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleSaveProfile}
                className="bg-teal text-white px-4 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-save-line mr-2"></i>
                Save Changes
              </button>
              <button
                onClick={() => {
                  setEditingProfile(false);
                  // Reload original values
                  supabase.auth.getSession().then(({ data: { session } }) => {
                    if (session) {
                      const metadata = session.user.user_metadata;
                      setUserProfile({
                        firstName: metadata.first_name || '',
                        lastName: metadata.last_name || '',
                        email: session.user.email || '',
                        phone: metadata.phone || '',
                        nmls: metadata.nmls || ''
                      });
                    }
                  });
                }}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        {/* Pre-Approval Letter Template Section */}
        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6 mb-6`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Pre-Approval Letter Template</h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Customize the template used when generating pre-approval PDFs from the CRM page</p>
            </div>
            {!editingPreApproval && (
              <button
                onClick={() => setEditingPreApproval(true)}
                className="bg-teal text-white px-4 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-edit-line mr-2"></i>
                Edit Template
              </button>
            )}
          </div>

          {preApprovalSaveMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg text-sm"
            >
              <i className="ri-check-line mr-2"></i>
              {preApprovalSaveMessage}
            </motion.div>
          )}

          <div className={`mb-4 p-4 ${isDarkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'} border rounded-lg`}>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`ri-information-line text-xl ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}></i>
              </div>
              <div className="flex-1">
                <h4 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'} mb-1`}>Available Variables</h4>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Use these variables in your template - they will be automatically replaced with client data:
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><code className="bg-white/50 px-2 py-0.5 rounded">{'{clientName}'}</code> - Client's full name</div>
                  <div><code className="bg-white/50 px-2 py-0.5 rounded">{'{firstName}'}</code> - Client's first name</div>
                  <div><code className="bg-white/50 px-2 py-0.5 rounded">{'{lastName}'}</code> - Client's last name</div>
                  <div><code className="bg-white/50 px-2 py-0.5 rounded">{'{loanAmount}'}</code> - Loan amount</div>
                  <div><code className="bg-white/50 px-2 py-0.5 rounded">{'{purchasePrice}'}</code> - Purchase price</div>
                  <div><code className="bg-white/50 px-2 py-0.5 rounded">{'{state}'}</code> - Property state</div>
                  <div><code className="bg-white/50 px-2 py-0.5 rounded">{'{loanType}'}</code> - Loan type</div>
                  <div><code className="bg-white/50 px-2 py-0.5 rounded">{'{brokerName}'}</code> - Broker's full name</div>
                  <div><code className="bg-white/50 px-2 py-0.5 rounded">{'{brokerEmail}'}</code> - Broker's email</div>
                  <div><code className="bg-white/50 px-2 py-0.5 rounded">{'{brokerFirstName}'}</code> - Broker's first name</div>
                  <div><code className="bg-white/50 px-2 py-0.5 rounded">{'{brokerLastName}'}</code> - Broker's last name</div>
                  <div><code className="bg-white/50 px-2 py-0.5 rounded">{'{brokerPhone}'}</code> - Broker's phone</div>
                  <div><code className="bg-white/50 px-2 py-0.5 rounded">{'{brokerNMLS}'}</code> - Broker's NMLS#</div>
                  <div><code className="bg-white/50 px-2 py-0.5 rounded">{'{todaysDate}'}</code> - Today's date</div>
                  <div><code className="bg-white/50 px-2 py-0.5 rounded">{'{expirationDate}'}</code> - 90 days from today</div>
                </div>
              </div>
            </div>
          </div>

          {editingPreApproval ? (
            <div>
              <textarea
                value={preApprovalTemplate}
                onChange={(e) => setPreApprovalTemplate(e.target.value)}
                rows={20}
                className={`w-full px-4 py-3 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm font-mono resize-none`}
                placeholder="Enter your pre-approval letter template..."
              />
              <div className="flex gap-2 mt-4">
                <button
                  onClick={handleSavePreApprovalTemplate}
                  className="bg-teal text-white px-4 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-save-line mr-2"></i>
                  Save Template
                </button>
                <button
                  onClick={() => {
                    setEditingPreApproval(false);
                    loadPreApprovalTemplate();
                  }}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg p-4`}>
              <pre className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'} whitespace-pre-wrap font-mono`}>
                {preApprovalTemplate}
              </pre>
            </div>
          )}
        </div>

        {/* Comp Rates Section */}
        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6 mb-6`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Compensation Rates</h2>
            <button
              onClick={() => setShowAddCompForm(!showAddCompForm)}
              className={`bg-teal text-white px-4 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm ${isDarkMode ? 'text-teal' : 'text-white'}`}
            >
              <i className="ri-add-line mr-2"></i>
              Add Comp Rate
            </button>
          </div>

          {showAddCompForm && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-50 rounded-lg p-4 mb-4"
            >
              <div className="grid grid-cols-5 gap-3 mb-3">
                <input
                  type="text"
                  placeholder="Broker Abbreviation (e.g., NS, TM)"
                  value={newCompRate.broker}
                  onChange={(e) => setNewCompRate({ ...newCompRate, broker: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                />
                <input
                  type="text"
                  placeholder="Full Broker Name"
                  value={newCompRate.full_broker_name}
                  onChange={(e) => setNewCompRate({ ...newCompRate, full_broker_name: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                />
                <input
                  type="email"
                  placeholder="Email Address"
                  value={newCompRate.email_address}
                  onChange={(e) => setNewCompRate({ ...newCompRate, email_address: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Company Comp % (e.g., 1.5)"
                  value={newCompRate.comp_percentage}
                  onChange={(e) => setNewCompRate({ ...newCompRate, comp_percentage: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                />
                <input
                  type="number"
                  step="0.01"
                  placeholder="Personal Comp % (e.g., 0.5)"
                  value={newCompRate.personal_comp_percentage}
                  onChange={(e) => setNewCompRate({ ...newCompRate, personal_comp_percentage: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleAddCompRate}
                  className="bg-teal text-white px-4 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setShowAddCompForm(false);
                    setNewCompRate({ broker: '', comp_percentage: '', personal_comp_percentage: '', full_broker_name: '', email_address: '' });
                  }}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <tr>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Broker Abbreviation</th>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Full Broker Name</th>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Email Address</th>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Company Comp %</th>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Personal Comp %</th>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Personal Comp % of Total</th>
                  <th className={`px-4 py-3 text-center text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {compRates.map((rate, index) => (
                  <tr key={rate.id} className={isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-3">
                      {editingCompId === rate.id ? (
                        <input
                          type="text"
                          value={rate.broker}
                          onChange={(e) => setCompRates(compRates.map(r => r.id === rate.id ? { ...r, broker: e.target.value } : r))}
                          className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                        />
                      ) : (
                        <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{rate.broker}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingCompId === rate.id ? (
                        <input
                          type="text"
                          value={rate.full_broker_name}
                          onChange={(e) => setCompRates(compRates.map(r => r.id === rate.id ? { ...r, full_broker_name: e.target.value } : r))}
                          className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                        />
                      ) : (
                        <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{rate.full_broker_name}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingCompId === rate.id ? (
                        <input
                          type="email"
                          value={rate.email_address || ''}
                          onChange={(e) => setCompRates(compRates.map(r => r.id === rate.id ? { ...r, email_address: e.target.value } : r))}
                          className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                        />
                      ) : (
                        <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{rate.email_address || '-'}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingCompId === rate.id ? (
                        <input
                          type="number"
                          step="0.01"
                          value={rate.comp_percentage}
                          onChange={(e) => setCompRates(compRates.map(r => r.id === rate.id ? { ...r, comp_percentage: parseFloat(e.target.value) } : r))}
                          className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                        />
                      ) : (
                        <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{rate.comp_percentage}%</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingCompId === rate.id ? (
                        <input
                          type="number"
                          step="0.01"
                          value={rate.personal_comp_percentage}
                          onChange={(e) => setCompRates(compRates.map(r => r.id === rate.id ? { ...r, personal_comp_percentage: parseFloat(e.target.value) } : r))}
                          className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                        />
                      ) : (
                        <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{rate.personal_comp_percentage}%</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-semibold text-teal">
                        {((rate.comp_percentage * rate.personal_comp_percentage) / 100).toFixed(2)}%
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        {editingCompId === rate.id ? (
                          <>
                            <button
                              onClick={() => handleSaveCompRate(rate.id)}
                              className="w-7 h-7 flex items-center justify-center bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors cursor-pointer"
                            >
                              <i className="ri-check-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => setEditingCompId(null)}
                              className="w-7 h-7 flex items-center justify-center bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
                            >
                              <i className="ri-close-line text-sm"></i>
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleMoveCompRateUp(rate.id)}
                              disabled={index === 0}
                              className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                                index === 0 
                                  ? 'bg-gray-100 text-gray-300 cursor-not-allowed' 
                                  : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                              }`}
                              title="Move Up"
                            >
                              <i className="ri-arrow-up-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => handleMoveCompRateDown(rate.id)}
                              disabled={index >= compRates.length - 1}
                              className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                                index >= compRates.length - 1
                                  ? 'bg-gray-100 text-gray-300 cursor-not-allowed' 
                                  : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                              }`}
                              title="Move Down"
                            >
                              <i className="ri-arrow-down-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => setEditingCompId(rate.id)}
                              className="w-7 h-7 flex items-center justify-center bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors cursor-pointer"
                            >
                              <i className="ri-edit-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => handleDeleteCompRate(rate.id)}
                              className="w-7 h-7 flex items-center justify-center bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors cursor-pointer"
                            >
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Page Order Section */}
        {isAdmin && (
        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6 mb-6`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Navigation Pages Order</h2>
          </div>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Drag pages up or down to change their order in the navigation dropdown</p>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <tr>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Page Name</th>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Display Order</th>
                  <th className={`px-4 py-3 text-center text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {pageOrders.map((page, index) => (
                  <tr key={page.id} className={isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-3">
                      <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{page.page_name}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{page.display_order}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleMovePageUp(page.id)}
                          disabled={index === 0}
                          className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                            index === 0 
                              ? 'bg-gray-100 text-gray-300 cursor-not-allowed' 
                              : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                          }`}
                          title="Move Up"
                        >
                          <i className="ri-arrow-up-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => handleMovePageDown(page.id)}
                          disabled={index >= pageOrders.length - 1}
                          className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                            index >= pageOrders.length - 1
                              ? 'bg-gray-100 text-gray-300 cursor-not-allowed' 
                              : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                          }`}
                          title="Move Down"
                        >
                          <i className="ri-arrow-down-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Client States Section */}
        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6 mb-6`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Client States</h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                {settingsView === 'personal' 
                  ? 'Your personal client states and global defaults. Click the eye icon to hide global defaults you don\'t use.' 
                  : 'Global default client states available to all users'}
              </p>
            </div>
            <button
              onClick={() => setShowAddStateForm(!showAddStateForm)}
              className={`bg-teal text-white px-4 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm ${isDarkMode ? 'text-teal' : 'text-white'}`}
            >
              <i className="ri-add-line mr-2"></i>
              Add {settingsView === 'personal' ? 'Personal' : 'Global'} State
            </button>
          </div>

          {showAddStateForm && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-50 rounded-lg p-4 mb-4"
            >
              <div className="mb-3">
                <input
                  type="text"
                  placeholder="State Name"
                  value={newClientState.state_name}
                  onChange={(e) => setNewClientState({ ...newClientState, state_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleAddClientState}
                  className="bg-teal text-white px-4 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setShowAddStateForm(false);
                    setNewClientState({ state_name: '', display_order: '' });
                  }}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <tr>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>State Name</th>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Type</th>
                  {settingsView === 'personal' && (
                    <th className={`px-4 py-3 text-center text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Status</th>
                  )}
                  <th className={`px-4 py-3 text-center text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {clientStates.map((state, index) => {
                  const isGlobal = state.is_global === true;
                  const isPersonal = !isGlobal && state.user_id === currentUserId;
                  const isHidden = isGlobal && isGlobalSettingHidden('client_state', state.id);
                  
                  return (
                    <tr key={state.id} className={`${isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'} ${isHidden ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3">
                        {editingStateId === state.id ? (
                          <input
                            type="text"
                            value={state.state_name}
                            onChange={(e) => setClientStates(clientStates.map(s => s.id === state.id ? { ...s, state_name: e.target.value } : s))}
                            className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                          />
                        ) : (
                          <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'} ${isHidden ? 'line-through' : ''}`}>{state.state_name}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isGlobal ? (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                            <i className="ri-global-line mr-1"></i>
                            Global
                          </span>
                        ) : isPersonal ? (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-teal/20 text-teal' : 'bg-teal/10 text-teal'}`}>
                            <i className="ri-user-line mr-1"></i>
                            Personal
                          </span>
                        ) : null}
                      </td>
                      {settingsView === 'personal' && (
                        <td className="px-4 py-3 text-center">
                          {isGlobal && (
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              isHidden 
                                ? (isDarkMode ? 'bg-orange-900/30 text-orange-400' : 'bg-orange-100 text-orange-700')
                                : (isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700')
                            }`}>
                              <i className={`${isHidden ? 'ri-eye-off-line' : 'ri-eye-line'} mr-1`}></i>
                              {isHidden ? 'Hidden' : 'Visible'}
                            </span>
                          )}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          {editingStateId === state.id ? (
                            <>
                              <button
                                onClick={() => handleSaveClientState(state.id)}
                                className="w-7 h-7 flex items-center justify-center bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors cursor-pointer"
                              >
                                <i className="ri-check-line text-sm"></i>
                              </button>
                              <button
                                onClick={() => setEditingStateId(null)}
                                className="w-7 h-7 flex items-center justify-center bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
                              >
                                <i className="ri-close-line text-sm"></i>
                              </button>
                            </>
                          ) : (
                            <>
                              {settingsView === 'personal' && isGlobal && (
                                <button
                                  onClick={() => handleToggleGlobalSettingVisibility('client_state', state.id)}
                                  className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                                    isHidden 
                                      ? 'bg-orange-100 text-orange-600 hover:bg-orange-200' 
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  }`}
                                  title={isHidden ? 'Show this global default' : 'Hide this global default'}
                                >
                                  <i className={`${isHidden ? 'ri-eye-line' : 'ri-eye-off-line'} text-sm`}></i>
                                </button>
                              )}
                              <button
                                onClick={() => handleMoveStateUp(state.id)}
                                disabled={index === 0}
                                className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                                  index === 0 
                                    ? 'bg-gray-100 text-gray-300 cursor-not-allowed' 
                                    : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                                }`}
                                title="Move Up"
                              >
                                <i className="ri-arrow-up-line text-sm"></i>
                              </button>
                              <button
                                onClick={() => handleMoveStateDown(state.id)}
                                disabled={index >= clientStates.length - 1}
                                className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                                  index >= clientStates.length - 1
                                    ? 'bg-gray-100 text-gray-300 cursor-not-allowed' 
                                    : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                                }`}
                                title="Move Down"
                              >
                                <i className="ri-arrow-down-line text-sm"></i>
                              </button>
                              {settingsView === 'personal' && isGlobal && (
                                <button
                                  onClick={() => handleCopyGlobalStateToPersonal(state.id)}
                                  className="w-7 h-7 flex items-center justify-center bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition-colors cursor-pointer"
                                  title="Copy to Personal Settings"
                                >
                                  <i className="ri-file-copy-line text-sm"></i>
                                </button>
                              )}
                              {(!isGlobal || settingsView === 'global') && (
                                <>
                                  <button
                                    onClick={() => setEditingStateId(state.id)}
                                    className="w-7 h-7 flex items-center justify-center bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors cursor-pointer"
                                  >
                                    <i className="ri-edit-line text-sm"></i>
                                  </button>
                                  <button
                                    onClick={() => handleDeleteClientState(state.id)}
                                    className="w-7 h-7 flex items-center justify-center bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors cursor-pointer"
                                  >
                                    <i className="ri-delete-bin-line text-sm"></i>
                                  </button>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Lender Names Section */}
        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6 mb-6`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Lender Names</h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                {settingsView === 'personal' 
                  ? 'Your personal lender names and global defaults. Click the eye icon to hide global defaults you don\'t use.' 
                  : 'Global default lender names available to all users'}
              </p>
            </div>
            <button
              onClick={() => setShowAddLenderForm(!showAddLenderForm)}
              className={`bg-teal text-white px-4 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm ${isDarkMode ? 'text-teal' : 'text-white'}`}
            >
              <i className="ri-add-line mr-2"></i>
              Add {settingsView === 'personal' ? 'Personal' : 'Global'} Lender
            </button>
          </div>

          {showAddLenderForm && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-50 rounded-lg p-4 mb-4"
            >
              <div className="mb-3">
                <input
                  type="text"
                  placeholder="Lender Name"
                  value={newLenderName.lender_name}
                  onChange={(e) => setNewLenderName({ ...newLenderName, lender_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleAddLenderName}
                  className="bg-teal text-white px-4 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setShowAddLenderForm(false);
                    setNewLenderName({ lender_name: '', display_order: '' });
                  }}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <tr>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Lender Name</th>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Type</th>
                  {settingsView === 'personal' && (
                    <th className={`px-4 py-3 text-center text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Status</th>
                  )}
                  <th className={`px-4 py-3 text-center text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {lenderNames.map((lender, index) => {
                  const isGlobal = lender.is_global === true;
                  const isPersonal = !isGlobal && lender.user_id === currentUserId;
                  const isHidden = isGlobal && isGlobalSettingHidden('lender_name', lender.id);
                  
                  return (
                    <tr key={lender.id} className={`${isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'} ${isHidden ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3">
                        {editingLenderId === lender.id ? (
                          <input
                            type="text"
                            value={lender.lender_name}
                            onChange={(e) => setLenderNames(lenderNames.map(l => l.id === lender.id ? { ...l, lender_name: e.target.value } : l))}
                            className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                          />
                        ) : (
                          <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'} ${isHidden ? 'line-through' : ''}`}>{lender.lender_name}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isGlobal ? (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                            <i className="ri-global-line mr-1"></i>
                            Global
                          </span>
                        ) : isPersonal ? (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-teal/20 text-teal' : 'bg-teal/10 text-teal'}`}>
                            <i className="ri-user-line mr-1"></i>
                            Personal
                          </span>
                        ) : null}
                      </td>
                      {settingsView === 'personal' && (
                        <td className="px-4 py-3 text-center">
                          {isGlobal && (
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              isHidden 
                                ? (isDarkMode ? 'bg-orange-900/30 text-orange-400' : 'bg-orange-100 text-orange-700')
                                : (isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700')
                            }`}>
                              <i className={`${isHidden ? 'ri-eye-off-line' : 'ri-eye-line'} mr-1`}></i>
                              {isHidden ? 'Hidden' : 'Visible'}
                            </span>
                          )}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          {editingLenderId === lender.id ? (
                            <>
                              <button
                                onClick={() => handleSaveLenderName(lender.id)}
                                className="w-7 h-7 flex items-center justify-center bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors cursor-pointer"
                              >
                                <i className="ri-check-line text-sm"></i>
                              </button>
                              <button
                                onClick={() => setEditingLenderId(null)}
                                className="w-7 h-7 flex items-center justify-center bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
                              >
                                <i className="ri-close-line text-sm"></i>
                              </button>
                            </>
                          ) : (
                            <>
                              {settingsView === 'personal' && isGlobal && (
                                <button
                                  onClick={() => handleToggleGlobalSettingVisibility('lender_name', lender.id)}
                                  className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                                    isHidden 
                                      ? 'bg-orange-100 text-orange-600 hover:bg-orange-200' 
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  }`}
                                  title={isHidden ? 'Show this global default' : 'Hide this global default'}
                                >
                                  <i className={`${isHidden ? 'ri-eye-line' : 'ri-eye-off-line'} text-sm`}></i>
                                </button>
                              )}
                              <button
                                onClick={() => handleMoveLenderUp(lender.id)}
                                disabled={index === 0}
                                className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                                  index === 0 
                                    ? 'bg-gray-100 text-gray-300 cursor-not-allowed' 
                                    : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                                }`}
                                title="Move Up"
                              >
                                <i className="ri-arrow-up-line text-sm"></i>
                              </button>
                              <button
                                onClick={() => handleMoveLenderDown(lender.id)}
                                disabled={index >= lenderNames.length - 1}
                                className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                                  index >= lenderNames.length - 1
                                    ? 'bg-gray-100 text-gray-300 cursor-not-allowed' 
                                    : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                                }`}
                                title="Move Down"
                              >
                                <i className="ri-arrow-down-line text-sm"></i>
                              </button>
                              {settingsView === 'personal' && isGlobal && (
                                <button
                                  onClick={() => handleCopyGlobalLenderToPersonal(lender.id)}
                                  className="w-7 h-7 flex items-center justify-center bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition-colors cursor-pointer"
                                  title="Copy to Personal Settings"
                                >
                                  <i className="ri-file-copy-line text-sm"></i>
                                </button>
                              )}
                              {(!isGlobal || settingsView === 'global') && (
                                <>
                                  <button
                                    onClick={() => setEditingLenderId(lender.id)}
                                    className="w-7 h-7 flex items-center justify-center bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors cursor-pointer"
                                  >
                                    <i className="ri-edit-line text-sm"></i>
                                  </button>
                                  <button
                                    onClick={() => handleDeleteLenderName(lender.id)}
                                    className="w-7 h-7 flex items-center justify-center bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors cursor-pointer"
                                  >
                                    <i className="ri-delete-bin-line text-sm"></i>
                                  </button>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Loan Types Section */}
        {isAdmin && (
        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6 mb-6`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Loan Types</h2>
            <button
              onClick={() => setShowAddLoanTypeForm(!showAddLoanTypeForm)}
              className={`bg-teal text-white px-4 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm ${isDarkMode ? 'text-teal' : 'text-white'}`}
            >
              <i className="ri-add-line mr-2"></i>
              Add Loan Type
            </button>
          </div>

          {showAddLoanTypeForm && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-50 rounded-lg p-4 mb-4"
            >
              <div className="grid grid-cols-2 gap-3 mb-3">
                <input
                  type="text"
                  placeholder="Loan Type"
                  value={newLoanType.loan_type}
                  onChange={(e) => setNewLoanType({ ...newLoanType, loan_type: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                />
                <input
                  type="text"
                  placeholder="Intake Mapping"
                  value={newLoanType.intake_mapping}
                  onChange={(e) => setNewLoanType({ ...newLoanType, intake_mapping: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleAddLoanType}
                  className="bg-teal text-white px-4 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setShowAddLoanTypeForm(false);
                    setNewLoanType({ loan_type: '', display_order: '', intake_mapping: '' });
                  }}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <tr>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Loan Type</th>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Intake Mapping</th>
                  <th className={`px-4 py-3 text-center text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {loanTypes.map((type, index) => (
                  <tr key={type.id} className={isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-3">
                      {editingLoanTypeId === type.id ? (
                        <input
                          type="text"
                          value={type.loan_type}
                          onChange={(e) => setLoanTypes(loanTypes.map(t => t.id === type.id ? { ...t, loan_type: e.target.value } : t))}
                          className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                        />
                      ) : (
                        <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{type.loan_type}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingLoanTypeId === type.id ? (
                        <input
                          type="text"
                          value={type.intake_mapping}
                          onChange={(e) => setLoanTypes(loanTypes.map(t => t.id === type.id ? { ...t, intake_mapping: e.target.value } : t))}
                          className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                        />
                      ) : (
                        <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{type.intake_mapping}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        {editingLoanTypeId === type.id ? (
                          <>
                            <button
                              onClick={() => handleSaveLoanType(type.id)}
                              className="w-7 h-7 flex items-center justify-center bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors cursor-pointer"
                            >
                              <i className="ri-check-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => setEditingLoanTypeId(null)}
                              className="w-7 h-7 flex items-center justify-center bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
                            >
                              <i className="ri-close-line text-sm"></i>
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleMoveLoanTypeUp(type.id)}
                              disabled={index === 0}
                              className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                                index === 0 
                                  ? 'bg-gray-100 text-gray-300 cursor-not-allowed' 
                                  : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                              }`}
                              title="Move Up"
                            >
                              <i className="ri-arrow-up-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => handleMoveLoanTypeDown(type.id)}
                              disabled={index >= loanTypes.length - 1}
                              className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                                index >= loanTypes.length - 1
                                  ? 'bg-gray-100 text-gray-300 cursor-not-allowed' 
                                  : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                              }`}
                              title="Move Down"
                            >
                              <i className="ri-arrow-down-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => setEditingLoanTypeId(type.id)}
                              className="w-7 h-7 flex items-center justify-center bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors cursor-pointer"
                            >
                              <i className="ri-edit-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => handleDeleteLoanType(type.id)}
                              className="w-7 h-7 flex items-center justify-center bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors cursor-pointer"
                            >
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Lead Source Section */}
        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Lead Source</h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                {settingsView === 'personal' 
                  ? 'Your personal lead sources and global defaults. Click the eye icon to hide global defaults you don\'t use.' 
                  : 'Global default lead sources available to all users'}
              </p>
            </div>
            <button
              onClick={() => setShowAddLeadSourceForm(!showAddLeadSourceForm)}
              className={`bg-teal text-white px-4 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm ${isDarkMode ? 'text-teal' : 'text-white'}`}
            >
              <i className="ri-add-line mr-2"></i>
              Add {settingsView === 'personal' ? 'Personal' : 'Global'} Lead Source
            </button>
          </div>

          {showAddLeadSourceForm && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-50 rounded-lg p-4 mb-4"
            >
              <div className="grid grid-cols-2 gap-3 mb-3">
                <input
                  type="text"
                  placeholder="Name"
                  value={newLeadSource.name}
                  onChange={(e) => setNewLeadSource({ ...newLeadSource, name: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                />
                <input
                  type="email"
                  placeholder="Email (Optional)"
                  value={newLeadSource.email}
                  onChange={(e) => setNewLeadSource({ ...newLeadSource, email: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                />
                <input
                  type="tel"
                  placeholder="Phone Number (Optional)"
                  value={newLeadSource.phone}
                  onChange={(e) => setNewLeadSource({ ...newLeadSource, phone: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                />
                <input
                  type="text"
                  placeholder="State (Optional)"
                  value={newLeadSource.state}
                  onChange={(e) => setNewLeadSource({ ...newLeadSource, state: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleAddLeadSource}
                  className="bg-teal text-white px-4 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setShowAddLeadSourceForm(false);
                    setNewLeadSource({ name: '', email: '', phone: '', state: '', display_order: '' });
                  }}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <tr>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Name</th>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Email</th>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Phone Number</th>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>State</th>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Type</th>
                  {settingsView === 'personal' && (
                    <th className={`px-4 py-3 text-center text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Status</th>
                  )}
                  <th className={`px-4 py-3 text-center text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {leadSources.map((leadSource, index) => {
                  const isGlobal = leadSource.is_global === true;
                  const isPersonal = !isGlobal && leadSource.user_id === currentUserId;
                  const isHidden = isGlobal && isGlobalSettingHidden('lead_source', leadSource.id);
                  
                  return (
                    <tr key={leadSource.id} className={`${isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'} ${isHidden ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3">
                        {editingLeadSourceId === leadSource.id ? (
                          <input
                            type="text"
                            value={leadSource.name}
                            onChange={(e) => setLeadSources(leadSources.map(r => r.id === leadSource.id ? { ...r, name: e.target.value } : r))}
                            className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                          />
                        ) : (
                          <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'} ${isHidden ? 'line-through' : ''}`}>{leadSource.name}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {editingLeadSourceId === leadSource.id ? (
                          <input
                            type="email"
                            value={leadSource.email}
                            onChange={(e) => setLeadSources(leadSources.map(r => r.id === leadSource.id ? { ...r, email: e.target.value } : r))}
                            className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                          />
                        ) : (
                          <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{leadSource.email}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {editingLeadSourceId === leadSource.id ? (
                          <input
                            type="tel"
                            value={leadSource.phone}
                            onChange={(e) => setLeadSources(leadSources.map(r => r.id === leadSource.id ? { ...r, phone: e.target.value } : r))}
                            className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                          />
                        ) : (
                          <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{formatPhoneNumber(leadSource.phone)}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {editingLeadSourceId === leadSource.id ? (
                          <input
                            type="text"
                            value={leadSource.state}
                            onChange={(e) => setLeadSources(leadSources.map(r => r.id === leadSource.id ? { ...r, state: e.target.value } : r))}
                            className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                          />
                        ) : (
                          <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{leadSource.state}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isGlobal ? (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                            <i className="ri-global-line mr-1"></i>
                            Global
                          </span>
                        ) : isPersonal ? (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-teal/20 text-teal' : 'bg-teal/10 text-teal'}`}>
                            <i className="ri-user-line mr-1"></i>
                            Personal
                          </span>
                        ) : null}
                      </td>
                      {settingsView === 'personal' && (
                        <td className="px-4 py-3 text-center">
                          {isGlobal && (
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              isHidden 
                                ? (isDarkMode ? 'bg-orange-900/30 text-orange-400' : 'bg-orange-100 text-orange-700')
                                : (isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700')
                            }`}>
                              <i className={`${isHidden ? 'ri-eye-off-line' : 'ri-eye-line'} mr-1`}></i>
                              {isHidden ? 'Hidden' : 'Visible'}
                            </span>
                          )}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          {editingLeadSourceId === leadSource.id ? (
                            <>
                              <button
                                onClick={() => handleSaveLeadSource(leadSource.id)}
                                className="w-7 h-7 flex items-center justify-center bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors cursor-pointer"
                              >
                                <i className="ri-check-line text-sm"></i>
                              </button>
                              <button
                                onClick={() => setEditingLeadSourceId(null)}
                                className="w-7 h-7 flex items-center justify-center bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
                              >
                                <i className="ri-close-line text-sm"></i>
                              </button>
                            </>
                          ) : (
                            <>
                              {settingsView === 'personal' && isGlobal && (
                                <button
                                  onClick={() => handleToggleGlobalSettingVisibility('lead_source', leadSource.id)}
                                  className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                                    isHidden 
                                      ? 'bg-orange-100 text-orange-600 hover:bg-orange-200' 
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  }`}
                                  title={isHidden ? 'Show this global default' : 'Hide this global default'}
                                >
                                  <i className={`${isHidden ? 'ri-eye-line' : 'ri-eye-off-line'} text-sm`}></i>
                                </button>
                              )}
                              <button
                                onClick={() => handleMoveLeadSourceUp(leadSource.id)}
                                disabled={index === 0}
                                className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                                  index === 0 
                                    ? 'bg-gray-100 text-gray-300 cursor-not-allowed' 
                                    : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                                }`}
                                title="Move Up"
                              >
                                <i className="ri-arrow-up-line text-sm"></i>
                              </button>
                              <button
                                onClick={() => handleMoveLeadSourceDown(leadSource.id)}
                                disabled={index >= leadSources.length - 1}
                                className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                                  index >= leadSources.length - 1
                                    ? 'bg-gray-100 text-gray-300 cursor-not-allowed' 
                                    : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                                }`}
                                title="Move Down"
                              >
                                <i className="ri-arrow-down-line text-sm"></i>
                              </button>
                              {settingsView === 'personal' && isGlobal && (
                                <button
                                  onClick={() => handleCopyGlobalLeadSourceToPersonal(leadSource.id)}
                                  className="w-7 h-7 flex items-center justify-center bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition-colors cursor-pointer"
                                  title="Copy to Personal Settings"
                                >
                                  <i className="ri-file-copy-line text-sm"></i>
                                </button>
                              )}
                              {(!isGlobal || settingsView === 'global') && (
                                <>
                                  <button
                                    onClick={() => setEditingLeadSourceId(leadSource.id)}
                                    className="w-7 h-7 flex items-center justify-center bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors cursor-pointer"
                                  >
                                    <i className="ri-edit-line text-sm"></i>
                                  </button>
                                  <button
                                    onClick={() => handleDeleteLeadSource(leadSource.id)}
                                    className="w-7 h-7 flex items-center justify-center bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors cursor-pointer"
                                  >
                                    <i className="ri-delete-bin-line text-sm"></i>
                                  </button>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Discovery Call Template Section */}
        {isAdmin && (
        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6 mt-6`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Discovery Call Template</h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Customize the fields that appear in the discovery call form</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowDiscoveryPreview(true)}
                className={`${isDarkMode ? 'bg-blue-700 hover:bg-blue-800' : 'bg-blue-600 hover:bg-blue-700'} text-white px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer text-sm flex items-center gap-2`}
              >
                <i className="ri-eye-line"></i>
                Preview Template
              </button>
              <button
                onClick={() => setShowAddDiscoveryFieldForm(!showAddDiscoveryFieldForm)}
                className={`bg-teal text-white px-4 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm ${isDarkMode ? 'text-teal' : 'text-white'}`}
              >
                <i className="ri-add-line mr-2"></i>
                Add Field
              </button>
            </div>
          </div>

          {showAddDiscoveryFieldForm && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-50 rounded-lg p-4 mb-4"
            >
              <div className="grid grid-cols-2 gap-3 mb-3">
                <input
                  type="text"
                  placeholder="Field Name (e.g., property_type)"
                  value={newDiscoveryField.field_name}
                  onChange={(e) => setNewDiscoveryField({ ...newDiscoveryField, field_name: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                />
                <input
                  type="text"
                  placeholder="Field Label (e.g., Property Type)"
                  value={newDiscoveryField.field_label}
                  onChange={(e) => setNewDiscoveryField({ ...newDiscoveryField, field_label: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                />
                <select
                  value={newDiscoveryField.field_type}
                  onChange={(e) => setNewDiscoveryField({ ...newDiscoveryField, field_type: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                >
                  <option value="text">Text Input</option>
                  <option value="textarea">Text Area</option>
                  <option value="select">Dropdown</option>
                  <option value="date">Date</option>
                </select>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="new-field-required"
                    checked={newDiscoveryField.is_required}
                    onChange={(e) => setNewDiscoveryField({ ...newDiscoveryField, is_required: e.target.checked })}
                    className="w-4 h-4 text-teal border-gray-300 rounded focus:ring-teal cursor-pointer"
                  />
                  <label htmlFor="new-field-required" className="text-sm text-gray-700 cursor-pointer">Required Field</label>
                </div>
              </div>
              {newDiscoveryField.field_type === 'select' && (
                <div className="mb-3">
                  <input
                    type="text"
                    placeholder="Dropdown Options (comma-separated, e.g., Option 1, Option 2, Option 3)"
                    value={newDiscoveryField.dropdown_options}
                    onChange={(e) => setNewDiscoveryField({ ...newDiscoveryField, dropdown_options: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                  />
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={handleAddDiscoveryField}
                  className="bg-teal text-white px-4 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setShowAddDiscoveryFieldForm(false);
                    setNewDiscoveryField({ field_name: '', field_label: '', field_type: 'text', dropdown_options: '', is_required: false });
                  }}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <tr>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Field Label</th>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Column Name</th>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Field Type</th>
                  <th className={`px-4 py-3 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Dropdown Options</th>
                  <th className={`px-4 py-3 text-center text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Required</th>
                  <th className={`px-4 py-3 text-center text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {discoveryFields.map((field, index) => (
                  <tr key={field.id} className={isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-3">
                      {editingDiscoveryFieldId === field.id ? (
                        <input
                          type="text"
                          value={field.field_label}
                          onChange={(e) => setDiscoveryFields(discoveryFields.map(f => f.id === field.id ? { ...f, field_label: e.target.value } : f))}
                          className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                        />
                      ) : (
                        <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{field.field_label}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-mono ${isDarkMode ? 'text-blue-400' : 'text-blue-600'} bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded`}>
                        {field.field_name}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {editingDiscoveryFieldId === field.id ? (
                        <select
                          value={field.field_type}
                          onChange={(e) => setDiscoveryFields(discoveryFields.map(f => f.id === field.id ? { ...f, field_type: e.target.value } : f))}
                          className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                        >
                          <option value="text">Text Input</option>
                          <option value="textarea">Text Area</option>
                          <option value="select">Dropdown</option>
                          <option value="date">Date</option>
                        </select>
                      ) : (
                        <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'} capitalize`}>{field.field_type === 'select' ? 'Dropdown' : field.field_type === 'textarea' ? 'Text Area' : field.field_type === 'date' ? 'Date' : 'Text Input'}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingDiscoveryFieldId === field.id ? (
                        <input
                          type="text"
                          value={field.dropdown_options ? field.dropdown_options.join(', ') : ''}
                          onChange={(e) => setDiscoveryFields(discoveryFields.map(f => f.id === field.id ? { ...f, dropdown_options: e.target.value.split(',').map(opt => opt.trim()).filter(opt => opt) } : f))}
                          className={`w-full px-2 py-1 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'} rounded-md focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                          placeholder="Comma-separated options"
                          disabled={field.field_type !== 'select'}
                        />
                      ) : (
                        <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {field.dropdown_options && field.dropdown_options.length > 0 
                            ? field.dropdown_options.join(', ') 
                            : '-'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {editingDiscoveryFieldId === field.id ? (
                        <input
                          type="checkbox"
                          checked={field.is_required}
                          onChange={(e) => setDiscoveryFields(discoveryFields.map(f => f.id === field.id ? { ...f, is_required: e.target.checked } : f))}
                          className="w-4 h-4 text-teal border-gray-300 rounded focus:ring-teal cursor-pointer"
                        />
                      ) : (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          field.is_required ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {field.is_required ? 'Yes' : 'No'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        {editingDiscoveryFieldId === field.id ? (
                          <>
                            <button
                              onClick={() => handleSaveDiscoveryField(field.id)}
                              className="w-7 h-7 flex items-center justify-center bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors cursor-pointer"
                            >
                              <i className="ri-check-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => setEditingDiscoveryFieldId(null)}
                              className="w-7 h-7 flex items-center justify-center bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
                            >
                              <i className="ri-close-line text-sm"></i>
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleMoveDiscoveryFieldUp(field.id)}
                              disabled={index === 0}
                              className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                                index === 0 
                                  ? 'bg-gray-100 text-gray-300 cursor-not-allowed' 
                                  : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                              }`}
                              title="Move Up"
                            >
                              <i className="ri-arrow-up-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => handleMoveDiscoveryFieldDown(field.id)}
                              disabled={index >= discoveryFields.length - 1}
                              className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                                index >= discoveryFields.length - 1
                                  ? 'bg-gray-100 text-gray-300 cursor-not-allowed' 
                                  : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                              }`}
                              title="Move Down"
                            >
                              <i className="ri-arrow-down-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => setEditingDiscoveryFieldId(field.id)}
                              className="w-7 h-7 flex items-center justify-center bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors cursor-pointer"
                            >
                              <i className="ri-edit-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => handleDeleteDiscoveryField(field.id)}
                              className="w-7 h-7 flex items-center justify-center bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors cursor-pointer"
                            >
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Quick Filter Configuration Section */}
        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6 mt-6`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Quick Filter Configuration</h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {settingsView === 'personal' 
                  ? 'Your personal quick filters and global defaults. Click the eye icon to hide global defaults you don\'t use.' 
                  : 'Global default quick filters available to all users'}
              </p>
            </div>
            <button
              onClick={() => setShowAddQuickFilterForm(!showAddQuickFilterForm)}
              className={`bg-teal text-white px-4 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm ${isDarkMode ? 'text-teal' : 'text-white'}`}
            >
              <i className="ri-add-line mr-2"></i>
              Add {settingsView === 'personal' ? 'Personal' : 'Global'} Quick Filter
            </button>
          </div>

          {showAddQuickFilterForm && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-50 rounded-lg p-4 mb-4"
            >
              <div className="mb-3">
                <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-2`}>
                  Filter Name
                </label>
                <input
                  type="text"
                  placeholder="e.g., Hot Leads, Closing Soon"
                  value={newQuickFilterName}
                  onChange={(e) => setNewQuickFilterName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleAddQuickFilter}
                  className="bg-teal text-white px-4 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setShowAddQuickFilterForm(false);
                    setNewQuickFilterName('');
                  }}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          )}

          <div className="space-y-6">
            {quickFilterConfigs.map((config, index) => {
              const isGlobal = config.is_global === true;
              const isPersonal = !isGlobal && config.user_id === currentUserId;
              const isHidden = isGlobal && isGlobalSettingHidden('quick_filter', config.id);
              
              return (
                <div key={config.id} className={`border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} rounded-lg p-4 ${isHidden ? 'opacity-50' : ''}`}>
                  <div className="flex items-center justify-between mb-3">
                    {editingQuickFilterId === config.id ? (
                      <div className="flex items-center gap-2 flex-1">
                        <input
                          type="text"
                          value={config.filter_name}
                          onChange={(e) => setQuickFilterConfigs(quickFilterConfigs.map(c => 
                            c.id === config.id ? { ...c, filter_name: e.target.value } : c
                          ))}
                          className={`flex-1 px-3 py-2 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm`}
                        />
                        <button
                          onClick={() => {
                            handleUpdateFilterName(config.id, config.filter_name);
                            setEditingQuickFilterId(null);
                          }}
                          className="bg-green-100 text-green-600 px-3 py-2 rounded-lg hover:bg-green-200 transition-colors whitespace-nowrap cursor-pointer text-sm"
                        >
                          <i className="ri-check-line"></i>
                        </button>
                        <button
                          onClick={() => {
                            setEditingQuickFilterId(null);
                            loadQuickFilterConfigs();
                          }}
                          className="bg-gray-100 text-gray-600 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer text-sm"
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={() => handleMoveQuickFilterUp(config.id)}
                              disabled={index === 0}
                              className={`w-6 h-6 flex items-center justify-center rounded transition-colors cursor-pointer ${
                                index === 0 
                                  ? 'bg-gray-100 text-gray-300 cursor-not-allowed' 
                                  : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                              }`}
                              title="Move Up"
                            >
                              <i className="ri-arrow-up-line text-xs"></i>
                            </button>
                            <button
                              onClick={() => handleMoveQuickFilterDown(config.id)}
                              disabled={index >= quickFilterConfigs.length - 1}
                              className={`w-6 h-6 flex items-center justify-center rounded transition-colors cursor-pointer ${
                                index >= quickFilterConfigs.length - 1
                                  ? 'bg-gray-100 text-gray-300 cursor-not-allowed' 
                                  : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                              }`}
                              title="Move Down"
                            >
                              <i className="ri-arrow-down-line text-xs"></i>
                            </button>
                          </div>
                          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'} ${isHidden ? 'line-through' : ''}`}>{config.filter_name}</h3>
                          {isGlobal ? (
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                              <i className="ri-global-line mr-1"></i>
                              Global
                            </span>
                          ) : isPersonal ? (
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-teal/20 text-teal' : 'bg-teal/10 text-teal'}`}>
                              <i className="ri-user-line mr-1"></i>
                              Personal
                            </span>
                          ) : null}
                          {settingsView === 'personal' && isGlobal && (
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              isHidden 
                                ? (isDarkMode ? 'bg-orange-900/30 text-orange-400' : 'bg-orange-100 text-orange-700')
                                : (isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700')
                            }`}>
                              <i className={`${isHidden ? 'ri-eye-off-line' : 'ri-eye-line'} mr-1`}></i>
                              {isHidden ? 'Hidden' : 'Visible'}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {settingsView === 'personal' && isGlobal && (
                            <button
                              onClick={() => handleToggleGlobalSettingVisibility('quick_filter', config.id)}
                              className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer text-sm ${
                                isHidden 
                                  ? 'bg-orange-100 text-orange-600 hover:bg-orange-200' 
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                              title={isHidden ? 'Show this global default' : 'Hide this global default'}
                            >
                              <i className={`${isHidden ? 'ri-eye-line' : 'ri-eye-off-line'} mr-2`}></i>
                              {isHidden ? 'Show' : 'Hide'}
                            </button>
                          )}
                          {settingsView === 'personal' && isGlobal && (
                            <button
                              onClick={() => handleCopyGlobalQuickFilterToPersonal(config.id)}
                              className="bg-purple-100 text-purple-600 px-4 py-2 rounded-lg font-medium hover:bg-purple-200 transition-colors whitespace-nowrap cursor-pointer text-sm"
                              title="Copy to Personal Settings"
                            >
                              <i className="ri-file-copy-line mr-2"></i>
                              Copy to Personal
                            </button>
                          )}
                          {(!isGlobal || settingsView === 'global') && (
                            <>
                              <button
                                onClick={() => setEditingQuickFilterId(config.id)}
                                className="text-blue-600 hover:text-blue-700 transition-colors cursor-pointer text-sm font-medium"
                              >
                                <i className="ri-edit-line mr-1"></i>
                                Rename
                              </button>
                              <button
                                onClick={() => handleSaveQuickFilterConfig(config.id)}
                                className="bg-teal text-white px-4 py-2 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer text-sm"
                              >
                                <i className="ri-save-line mr-2"></i>
                                Save Configuration
                              </button>
                              <button
                                onClick={() => handleDeleteQuickFilter(config.id)}
                                className="bg-red-100 text-red-600 px-4 py-2 rounded-lg font-medium hover:bg-red-200 transition-colors whitespace-nowrap cursor-pointer text-sm"
                              >
                                <i className="ri-delete-bin-line mr-2"></i>
                                Delete Filter
                              </button>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  {(!isGlobal || settingsView === 'global') && (
                    <div className="mb-3">
                      <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-2`}>
                        Add Client State to {config.filter_name}
                      </label>
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            handleAddClientStateToFilter(config.id, e.target.value);
                            e.target.value = '';
                          }
                        }}
                        className={`w-full px-3 py-2 border ${isDarkMode ? 'border-gray-600 bg-gray-700 text-gray-200' : 'border-gray-300 bg-white text-gray-900'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer`}
                      >
                        <option value="">Select a client state to add...</option>
                        {clientStates
                          .filter(opt => !config.client_states.includes(opt.state_name))
                          .map((opt) => (
                            <option key={opt.state_name} value={opt.state_name}>
                              {opt.state_name}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-2`}>
                      Client States in {config.filter_name}
                    </label>
                    <div className="space-y-2">
                      {config.client_states.length > 0 ? (
                        config.client_states.map((stateName) => (
                          <div
                            key={stateName}
                            className={`flex items-center justify-between p-3 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg`}
                          >
                            <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{stateName}</span>
                            {(!isGlobal || settingsView === 'global') && (
                              <button
                                onClick={() => handleRemoveClientStateFromFilter(config.id, stateName)}
                                className="w-7 h-7 flex items-center justify-center bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors cursor-pointer"
                                title="Remove from filter"
                              >
                                <i className="ri-close-line text-sm"></i>
                              </button>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className={`text-center py-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg`}>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No client states configured for this filter</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={`mt-4 p-4 ${isDarkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'} border rounded-lg`}>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`ri-information-line text-xl ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}></i>
              </div>
              <div className="flex-1">
                <h4 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'} mb-1`}>About Quick Filters</h4>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Quick filters on the CRM page allow you to quickly view clients based on their current state. 
                  Configure which client states belong to each filter here. Changes will take effect immediately on the CRM page.
                  Hidden global filters will not appear in your CRM quick filter options.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Discovery Call Preview Modal */}
      {showDiscoveryPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`${isDarkMode ? 'bg-[#212936]' : 'bg-white'} rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto`}
          >
            <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className={`text-xl font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Discovery Call Template Preview</h3>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>This is how the discovery call form will appear to users</p>
                </div>
                <button
                  onClick={() => setShowDiscoveryPreview(false)}
                  className={`w-8 h-8 flex items-center justify-center ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} transition-colors cursor-pointer`}
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {discoveryFields
                  .filter(field => field.field_name !== 'mortgagee_section')
                  .map((field) => {
                    // Check if this is the field right before mortgagees section
                    const isFieldBeforeMortgagees = field.field_name === 'preapproval_status';
                    
                    return (
                      <>
                        <div key={field.id} className={field.field_type === 'textarea' ? 'md:col-span-2' : ''}>
                          <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                            {field.field_label}
                            {field.is_required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          {field.field_type === 'select' ? (
                            <select
                              disabled
                              className={`w-full px-4 py-2.5 border ${isDarkMode ? 'bg-[#394150] border-gray-600 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-not-allowed opacity-60`}
                            >
                              <option value="">Select...</option>
                              {field.dropdown_options?.map((option) => (
                                <option key={option} value={option}>{option}</option>
                              ))}
                            </select>
                          ) : field.field_type === 'textarea' ? (
                            <textarea
                              disabled
                              placeholder={`Enter ${field.field_label.toLowerCase()}...`}
                              rows={4}
                              className={`w-full px-4 py-2.5 border ${isDarkMode ? 'bg-[#394150] border-gray-600 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm resize-none cursor-not-allowed opacity-60`}
                            />
                          ) : field.field_type === 'date' ? (
                            <input
                              type="date"
                              disabled
                              className={`w-full px-4 py-2.5 border ${isDarkMode ? 'bg-[#394150] border-gray-600 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-not-allowed opacity-60`}
                            />
                          ) : (
                            <input
                              type="text"
                              disabled
                              placeholder={`e.g., ${field.field_label}`}
                              className={`w-full px-4 py-2.5 border ${isDarkMode ? 'bg-[#394150] border-gray-600 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-not-allowed opacity-60`}
                            />
                          )}
                        </div>
                        
                        {/* Insert Mortgagees Section right after preapproval_status field */}
                        {isFieldBeforeMortgagees && (
                          <div className="md:col-span-2">
                            <div className={`mt-6 pt-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                              <div className="flex items-center justify-between mb-4">
                                <h4 className={`text-lg font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Mortgagee Information</h4>
                                <button
                                  disabled
                                  className={`${isDarkMode ? 'bg-teal/20 text-teal' : 'bg-teal/10 text-teal'} px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap cursor-not-allowed opacity-60 text-sm flex items-center gap-2`}
                                >
                                  <i className="ri-add-line"></i>
                                  Add Mortgagee
                                </button>
                              </div>

                              {/* Who Will be on Mortgage field */}
                              <div className="mb-4">
                                <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                  Who Will be on Mortgage?
                                </label>
                                <input
                                  type="text"
                                  disabled
                                  placeholder="Enter names of people who will be on the mortgage"
                                  className={`w-full px-4 py-2.5 border ${isDarkMode ? 'bg-[#394150] border-gray-600 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-not-allowed opacity-60`}
                                />
                              </div>

                              <div className={`mb-6 p-4 ${isDarkMode ? 'bg-[#394150] border-gray-600' : 'bg-gray-50 border-gray-200'} border rounded-lg`}>
                                <div className="flex items-center justify-between mb-4">
                                  <h5 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                                    Mortgagee 1
                                  </h5>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                  <div className="md:col-span-2">
                                    <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                      Mortgagee Name
                                    </label>
                                    <input
                                      type="text"
                                      disabled
                                      placeholder="Enter name"
                                      className={`w-full px-4 py-2.5 border ${isDarkMode ? 'bg-[#212936] border-gray-600 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-not-allowed opacity-60`}
                                    />
                                  </div>
                                  <div className="md:col-span-2">
                                    <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                      Credit Score Range
                                    </label>
                                    <select
                                      disabled
                                      className={`w-full px-4 py-2.5 border ${isDarkMode ? 'bg-[#212936] border-gray-600 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-not-allowed opacity-60`}
                                    >
                                      <option value="">Select...</option>
                                      <option value="Excellent (740+)">Excellent (740+)</option>
                                      <option value="Good (670-739)">Good (670-739)</option>
                                      <option value="Fair (580-669)">Fair (580-669)</option>
                                      <option value="Poor (&lt;580)">Poor (&lt;580)</option>
                                      <option value="Unknown">Unknown</option>
                                    </select>
                                  </div>
                                </div>

                                <div className={`border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-300'} pt-4`}>
                                  <div className="flex items-center justify-between mb-3">
                                    <h6 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      Employment & Income
                                    </h6>
                                    <button
                                      disabled
                                      className={`${isDarkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-600'} px-3 py-1.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-not-allowed opacity-60 text-xs flex items-center gap-1`}
                                    >
                                      <i className="ri-add-line"></i>
                                      Add Job
                                    </button>
                                  </div>

                                  <div className={`mb-3 p-3 ${isDarkMode ? 'bg-[#212936] border-gray-700' : 'bg-white border-gray-200'} border rounded-lg`}>
                                    <div className="flex items-center justify-between mb-2">
                                      <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        Job 1
                                      </span>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      <div>
                                        <label className={`block text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                                          Employment Status
                                        </label>
                                        <select
                                          disabled
                                          className={`w-full px-3 py-2 border ${isDarkMode ? 'bg-[#394150] border-gray-600 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-xs cursor-not-allowed opacity-60`}
                                        >
                                          <option value="">Select...</option>
                                          <option value="Full-time W2">Full-time W2</option>
                                          <option value="Self-employed">Self-employed</option>
                                          <option value="Part-time">Part-time</option>
                                          <option value="Contract">Contract</option>
                                          <option value="Retired">Retired</option>
                                          <option value="Other">Other</option>
                                        </select>
                                      </div>
                                      <div>
                                        <label className={`block text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                                          Current Income
                                        </label>
                                        <input
                                          type="text"
                                          disabled
                                          placeholder="e.g., $75,000/year"
                                          className={`w-full px-3 py-2 border ${isDarkMode ? 'bg-[#394150] border-gray-600 text-gray-200' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-xs cursor-not-allowed opacity-60`}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })}
              </div>

              <div className={`p-4 ${isDarkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'} border rounded-lg`}>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <strong className={isDarkMode ? 'text-gray-200' : 'text-gray-900'}>Note:</strong> This is a preview of how the discovery call form will appear. The actual form will be interactive and allow data entry.
                </p>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowDiscoveryPreview(false)}
                  className={`${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} px-6 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer text-sm`}
                >
                  Close Preview
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}